import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Input,
  Space,
  message,
  Tag,
  InputNumber,
  Popconfirm,
  Select,
  AutoComplete,
  Checkbox,
  Tooltip,
  Card,
  List,
  Empty,
  Spin,
  Alert,
  Tabs,
  Divider,
  Statistic,
} from "antd";
import {
  DatabaseOutlined,
  CloudOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditFilled,
  DeleteFilled,
  PlusOutlined,
} from "@ant-design/icons";
import { Pagination } from "antd";
import { serviceApi } from "../services/serviceApi";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { accountService } from "../services/accountService";
import { databricksService } from "../services/databricksService";
import IsEnabled from "../rolepermission";
import { useTheme } from '../context/ThemeContext';

// Debounce function to limit API calls
const useDebounce = (callback, delay) => {
  const timeoutRef = React.useRef(null);

  const debouncedCallback = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay],
  );

  return debouncedCallback;
};

/** Default CloudWatch period / scrape window for new metrics (seconds). */
const DEFAULT_SCRAPE_PERIOD = 300;
const DEFAULT_SCRAPE_LENGTH = 600;
const ACCOUNT_WIDE_METRICS_SERVICE_TYPES = new Set([
  "Glue",
  "AWS/Glue",
  "AWS/StepFunctions",
  "AWS/ElasticMapReduce",
  "AWS/MWAA",
  "AmazonMWAA",
]);

const getCategoryForMetric = (metricName = "") => {
  if (metricName.includes("Bytes") || metricName.includes("Size")) return "storage";
  if (metricName.includes("Requests") || metricName.includes("Count")) return "requests";
  if (
    metricName.includes("Latency") ||
    metricName.includes("CPU") ||
    metricName.includes("ResponseTime")
  ) {
    return "performance";
  }
  if (
    metricName.includes("Error") ||
    metricName.includes("4XX") ||
    metricName.includes("5XX")
  ) {
    return "error_detection";
  }
  if (metricName.includes("Host") || metricName.includes("Capacity")) {
    return "resource_utilization";
  }
  return "other";
};

const categorizeMetrics = (metrics = []) =>
  metrics.reduce((acc, metric) => {
    const category = metric.category || getCategoryForMetric(metric.name);
    if (!acc[category]) acc[category] = [];
    acc[category].push(metric);
    return acc;
  }, {});

function MetricsSelector({
  value = [],
  onChange,
  metricOptions = [],
  metricsLoading = false,
  onEnsureMetricsLoaded,
  onMetricsSaved,
  serviceType = "",
}) {
  const { isDark } = useTheme();

      const [searchText, setSearchText] = useState("");
      const [modalVisible, setModalVisible] = useState(false);
      const [tempSelectedMetrics, setTempSelectedMetrics] = useState([]);
      const [viewMode, setViewMode] = useState("smart");
      const [selectedCategory, setSelectedCategory] = useState("all");

      // Initialize temp metrics when modal opens
      const handleOpenModal = useCallback(() => {
        if (onEnsureMetricsLoaded && onEnsureMetricsLoaded() === false) {
          return;
        }
        setTempSelectedMetrics(value ? JSON.parse(JSON.stringify(value)) : []);
        setModalVisible(true);
      }, [value, onEnsureMetricsLoaded]);

      const handleModalCancel = useCallback(() => {
        setModalVisible(false);
        setSearchText("");
        setViewMode("smart");
        setSelectedCategory("all");
      }, []);

      const handleModalOk = useCallback(() => {
        const metricsToSave = tempSelectedMetrics.map((m) => ({
          name: m.name,
          statistics: [...m.statistics],
        }));
        onChange(metricsToSave);
        onMetricsSaved?.(metricsToSave);
        setModalVisible(false);
      }, [tempSelectedMetrics, onChange, onMetricsSaved]);

      const filterMetrics = useCallback((metrics, searchText) => {
        const search = searchText.toLowerCase().trim();
        if (!search) return metrics;
        if (search.length < 3) {
          return metrics.filter((m) => m.name.toLowerCase().startsWith(search));
        }
        return metrics.filter((m) => m.name.toLowerCase().includes(search));
      }, []);

      const handleMetricToggle = useCallback(
        (metric, checked) => {
          setTempSelectedMetrics((prev) => {
            if (checked) {
              if (prev.some((m) => m.name === metric.name)) {
                return prev;
              }
              const availableStats =
                metricOptions.find((m) => m.name === metric.name)?.statistics ||
                [];
              return [
                ...prev,
                { name: metric.name, statistics: availableStats },
              ];
            } else {
              return prev.filter((m) => m.name !== metric.name);
            }
          });
        },
        [metricOptions],
      );

      const handleStatisticsChange = useCallback(
        (metricName, statName, event) => {
          const isChecked = event.target.checked;
          setTempSelectedMetrics((prev) =>
            prev.map((metric) => {
              if (metric.name === metricName) {
                const newStats = isChecked
                  ? [...new Set([...(metric.statistics || []), statName])]
                  : (metric.statistics || []).filter((s) => s !== statName);
                return { ...metric, statistics: newStats };
              }
              return metric;
            }),
          );
        },
        [],
      );

      const handleSelectAllFiltered = useCallback(
        (filteredMetrics) => {
          const filteredMetricNames = filteredMetrics.map((m) => m.name);
          const allSelected = filteredMetrics.every((metric) =>
            tempSelectedMetrics.some(
              (selected) => selected.name === metric.name,
            ),
          );

          setTempSelectedMetrics((prev) => {
            if (allSelected) {
              return prev.filter((m) => !filteredMetricNames.includes(m.name));
            } else {
              const otherSelections = prev.filter(
                (m) => !filteredMetricNames.includes(m.name),
              );
              const filteredSelections = filteredMetrics.map((metric) => ({
                name: metric.name,
                statistics: metric.statistics || [],
              }));
              return [...otherSelections, ...filteredSelections];
            }
          });
        },
        [tempSelectedMetrics],
      );

      const areAllFilteredSelected = useCallback(
        (filteredMetrics) => {
          if (filteredMetrics.length === 0) return false;
          return filteredMetrics.every((metric) =>
            tempSelectedMetrics.some(
              (selected) => selected.name === metric.name,
            ),
          );
        },
        [tempSelectedMetrics],
      );

      const isMetricSelected = useCallback(
        (metricName) => {
          return tempSelectedMetrics.some((m) => m.name === metricName);
        },
        [tempSelectedMetrics],
      );

      const isStatisticSelected = useCallback(
        (metricName, statName) => {
          const metric = tempSelectedMetrics.find((m) => m.name === metricName);
          return metric?.statistics?.includes(statName) || false;
        },
        [tempSelectedMetrics],
      );

      const renderSelectedMetricsSummary = useMemo(() => {
        if (!value || value.length === 0) {
          return <div style={{ color: "#999" }}>No metrics selected</div>;
        }

        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              maxHeight: "60px",
              overflowY: "auto",
            }}
          >
            {value.map((metric) => (
              <Tag
                key={metric.name}
                color="blue"
                style={{ marginRight: 0 }}
                title={`${metric.name} (${metric.statistics?.join(", ") || "No statistics"})`}
              >
                {metric.name}
              </Tag>
            ))}

            {value.length > 0 && (
              <div
                style={{
                  fontSize: "12px",
                  color: isDark ? "#adbac7" : "#666",
                  width: "100%",
                  marginTop: "4px",
                }}
              >
                {value.length} metrics selected
              </div>
            )}
          </div>
        );
      }, [value, isDark]);

      const modalContent = useMemo(() => {
        if (!modalVisible) return null;

        const metricCategories = categorizeMetrics(metricOptions);
        const allMetrics = Object.values(metricCategories).flat();
        const filteredMetrics = filterMetrics(allMetrics, searchText);

        const getAvailableStats = (metricName) => {
          const metric = metricOptions.find((m) => m.name === metricName);
          return metric?.statistics || [];
        };

        const getMetricCategory = (metric) => {
          return (
            metric.category || getCategoryForMetric(metric.name)
          ).toLowerCase();
        };

        const recommendedMetrics = allMetrics.filter((m) => m.isRecommended);
        const uniqueCategories = [
          ...new Set(recommendedMetrics.map((m) => getMetricCategory(m))),
        ].sort();

        const viewFilteredMetrics =
          viewMode === "smart"
            ? filteredMetrics.filter((m) => m.isRecommended)
            : filteredMetrics;

        const categoryFilteredMetrics =
          selectedCategory === "all"
            ? viewFilteredMetrics
            : viewFilteredMetrics.filter(
                (m) => getMetricCategory(m) === selectedCategory,
              );

        if (metricsLoading) {
          return (
            <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
              <Spin />
              <div style={{ marginTop: 12 }}>Loading metrics...</div>
            </div>
          );
        }

        if (allMetrics.length === 0) {
          return (
            <Empty
              description="No metrics available for this namespace"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          );
        }

        return (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Input
                bordered={false}
                placeholder="Search metrics..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: "100%" }}
                allowClear
              />
            </div>

            <div style={{ marginBottom: 16, display: "flex", gap: "8px" }}>
              <Button
                type={viewMode === "smart" ? "primary" : "default"}
                onClick={() => setViewMode("smart")}
                style={{ borderRadius: "6px" }}
              >
                Smart View
              </Button>
              <Button
                type={viewMode === "all" ? "primary" : "default"}
                onClick={() => setViewMode("all")}
                style={{ borderRadius: "6px" }}
              >
                All Metrics
              </Button>
            </div>

            <div
              style={{
                marginBottom: 16,
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {uniqueCategories.map((category) => {
                const categoryLabel = category
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ");

                return (
                  <Button
                    key={category}
                    type={selectedCategory === category ? "primary" : "default"}
                    onClick={() => setSelectedCategory(category)}
                    style={{
                      borderRadius: "20px",
                      background:
                        selectedCategory === category
                          ? isDark
                            ? "rgba(31, 111, 235, 0.2)"
                            : "#E3F2FD"
                          : isDark
                            ? "#21262d"
                            : "#fff",
                      color:
                        selectedCategory === category
                          ? isDark
                            ? "#58a6ff"
                            : "#1976D2"
                          : isDark
                            ? "#adbac7"
                            : "#666",
                      border:
                        selectedCategory === category
                          ? `1px solid ${isDark ? "#58a6ff" : "#1976D2"}`
                          : `1px solid ${isDark ? "#30363d" : "#d9d9d9"}`,
                    }}
                    ghost={selectedCategory !== category}
                  >
                    {categoryLabel}
                  </Button>
                );
              })}
              <Button
                type={selectedCategory === "all" ? "primary" : "default"}
                onClick={() => setSelectedCategory("all")}
                style={{
                  borderRadius: "20px",
                  background:
                    selectedCategory === "all"
                      ? isDark
                        ? "rgba(31, 111, 235, 0.2)"
                        : "#E3F2FD"
                      : isDark
                        ? "#21262d"
                        : "#fff",
                  color:
                    selectedCategory === "all"
                      ? isDark
                        ? "#58a6ff"
                        : "#1976D2"
                      : isDark
                        ? "#adbac7"
                        : "#666",
                  border:
                    selectedCategory === "all"
                      ? `1px solid ${isDark ? "#58a6ff" : "#1976D2"}`
                      : `1px solid ${isDark ? "#30363d" : "#d9d9d9"}`,
                }}
                ghost={selectedCategory !== "all"}
              >
                All Categories
              </Button>
            </div>

            {viewMode === "smart" && (
              <div
                style={{
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingBottom: "8px",
                  borderBottom: `1px solid ${isDark ? "#30363d" : "#f0f0f0"}`,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span className="metrics-selector-section-title">
                    Recommended for {serviceType || "AWS/AOSS"}
                  </span>
                  <Tag color="orange" style={{ margin: 0 }}>
                    Recommended
                  </Tag>
                </div>
                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    handleSelectAllFiltered(categoryFilteredMetrics)
                  }
                  style={{ padding: "0 8px", height: "auto" }}
                >
                  {areAllFilteredSelected(categoryFilteredMetrics)
                    ? "Clear All"
                    : "Select All"}
                </Button>
              </div>
            )}

            {viewMode === "all" && (
              <div
                style={{
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingBottom: "8px",
                  borderBottom: `1px solid ${isDark ? "#30363d" : "#f0f0f0"}`,
                }}
              >
                <span className="metrics-selector-section-title">
                  {selectedCategory === "all"
                    ? "All Metrics"
                    : selectedCategory
                        .split("_")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() + word.slice(1),
                        )
                        .join(" ")}
                  {categoryFilteredMetrics.length > 0 && (
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 400,
                        color: isDark ? "#8b949e" : "#999",
                        marginLeft: "8px",
                      }}
                    >
                      ({categoryFilteredMetrics.length})
                    </span>
                  )}
                </span>
                <Button
                  type="link"
                  size="small"
                  onClick={() =>
                    handleSelectAllFiltered(categoryFilteredMetrics)
                  }
                  disabled={categoryFilteredMetrics.length === 0}
                  style={{ padding: "0 8px", height: "auto" }}
                >
                  {areAllFilteredSelected(categoryFilteredMetrics)
                    ? "Clear All"
                    : "Select All"}
                </Button>
              </div>
            )}

            <div
              style={{
                height: "calc(70vh - 280px)",
                overflowY: "auto",
                paddingRight: "8px",
              }}
            >
              {categoryFilteredMetrics.map((metric) => {
                const selected = isMetricSelected(metric.name);
                const availableStats = getAvailableStats(metric.name);
                const category = getMetricCategory(metric);

                const categoryColors = {
                  performance: "#1976D2",
                  error_detection: "#D32F2F",
                  errors: "#D32F2F",
                  requests: "#7B1FA2",
                  business_critical: "#FF6F00",
                  resource_utilization: "#388E3C",
                  cost_optimization: "#00897B",
                  storage: "#388E3C",
                  other: "#616161",
                };

                const categoryLabel = category
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ");

                return (
                  <div
                    key={metric.name}
                    style={{
                      marginBottom: "16px",
                      padding: "12px",
                      background: isDark ? "#161b22" : "#fff",
                      border: `1px solid ${isDark ? "#30363d" : "#f0f0f0"}`,
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        onChange={(e) =>
                          handleMetricToggle(metric, e.target.checked)
                        }
                        style={{ marginTop: "2px" }}
                      />

                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "4px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "15px",
                              fontWeight: 600,
                              color: isDark ? "#e6edf3" : "#333",
                            }}
                          >
                            {metric.display_name || metric.name}
                          </span>
                          {metric.isRecommended && (
                            <Tag
                              color="gold"
                              style={{
                                margin: 0,
                                fontSize: "11px",
                                padding: "0 6px",
                                lineHeight: "18px",
                              }}
                            >
                              ★
                            </Tag>
                          )}
                        </div>

                        <div
                          style={{
                            fontSize: "13px",
                            color: isDark ? "#adbac7" : "#666",
                            marginBottom: "8px",
                          }}
                        >
                          {metric.description ||
                            `CloudWatch metric for monitoring ${metric.name}`}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: selected ? "12px" : "0",
                          }}
                        >
                          <Tag
                            style={{
                              margin: 0,
                              background: isDark ? "#30363d" : "#F5F5F5",
                              color: categoryColors[category] || (isDark ? "#adbac7" : "#666"),
                              border: "none",
                              fontSize: "12px",
                            }}
                          >
                            {categoryLabel}
                          </Tag>
                        </div>

                        {selected && availableStats.length > 0 && (
                          <div
                            style={{
                              paddingTop: "12px",
                              borderTop: `1px solid ${isDark ? "#30363d" : "#f0f0f0"}`,
                            }}
                          >
                            <div
                              style={{
                                marginBottom: "8px",
                                color: isDark ? "#adbac7" : "#666",
                                fontSize: "13px",
                                fontWeight: 500,
                              }}
                            >
                              Statistics:
                            </div>
                            <Space direction="horizontal" size={8} wrap>
                              {availableStats.map((stat) => (
                                <Checkbox
                                  key={stat}
                                  checked={isStatisticSelected(
                                    metric.name,
                                    stat,
                                  )}
                                  onChange={(e) =>
                                    handleStatisticsChange(metric.name, stat, e)
                                  }
                                >
                                  <span style={{ fontSize: "13px" }}>
                                    {stat}
                                  </span>
                                </Checkbox>
                              ))}
                            </Space>
                          </div>
                        )}

                        {selected && availableStats.length === 0 && (
                          <div
                            style={{
                              marginTop: "8px",
                              paddingTop: "8px",
                              borderTop: "1px solid #f0f0f0",
                              color: isDark ? "#8b949e" : "#999",
                              fontSize: "12px",
                              fontStyle: "italic",
                            }}
                          >
                            No statistics available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {categoryFilteredMetrics.length === 0 && (
                <Empty
                  description="No matching metrics found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>

            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: `1px solid ${isDark ? "#30363d" : "#f0f0f0"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span style={{ fontSize: "14px", color: isDark ? "#adbac7" : "#666" }}>
                  <strong style={{ color: isDark ? "#e6edf3" : "#333" }}>
                    {tempSelectedMetrics.length}
                  </strong>{" "}
                  metrics selected
                </span>
                <Button
                  type="link"
                  onClick={() =>
                    handleSelectAllFiltered(categoryFilteredMetrics)
                  }
                  style={{ padding: 0, height: "auto" }}
                  disabled={categoryFilteredMetrics.length === 0}
                >
                  {areAllFilteredSelected(categoryFilteredMetrics)
                    ? "Clear All"
                    : "Select All Visible"}
                </Button>
                <Button
                  type="link"
                  onClick={() => setTempSelectedMetrics([])}
                  style={{ padding: 0, height: "auto" }}
                  danger
                >
                  Clear All Selections
                </Button>
              </div>

              <Space>
                <Button onClick={handleModalCancel}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={handleModalOk}
                  disabled={
                    tempSelectedMetrics.length === 0 ||
                    tempSelectedMetrics.some((m) => !m.statistics?.length)
                  }
                >
                  Save Selection
                </Button>
              </Space>
            </div>
          </div>
        );
      }, [
        modalVisible,
        searchText,
        viewMode,
        selectedCategory,
        tempSelectedMetrics,
        metricOptions,
        metricsLoading,
        filterMetrics,
        handleSelectAllFiltered,
        areAllFilteredSelected,
        isMetricSelected,
        handleMetricToggle,
        isStatisticSelected,
        handleStatisticsChange,
        handleModalCancel,
        handleModalOk,
        serviceType,
        isDark,
      ]);

      return (
        <div className="metrics-selector">
          <div
            onClick={handleOpenModal}
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: "2px",
              padding: "4px 11px",
              minHeight: "32px",
              cursor: "pointer",
              background: isDark ? "#161b22" : "#fff",
              transition: "all 0.3s",
              position: "relative",
            }}
          >
            {renderSelectedMetricsSummary}
            <div
              style={{
                position: "absolute",
                right: "11px",
                top: "50%",
                transform: "translateY(-50%)",
                color: isDark ? "#8b949e" : "#999",
              }}
            >
            </div>
          </div>

          <Modal
            title="Select Metrics"
            open={modalVisible}
            onCancel={handleModalCancel}
            footer={null}
            width={800}
            destroyOnClose
            bodyStyle={{ padding: "16px" }}
          >
            {modalContent}
          </Modal>
        </div>
      );
}

const SERVICE_TYPE_TO_CLOUDWATCH_NAMESPACE = {
  "AWS/StepFunctions": "AWS/States",
};

const INTERNAL_CONFIG_SERVICE_TYPES = new Set([
  "AWS/StepFunctions",
]);

const cloudwatchNamespaceForServiceType = (serviceType = "") => {
  const baseType = String(serviceType).split(" (")[0].trim();
  return SERVICE_TYPE_TO_CLOUDWATCH_NAMESPACE[baseType] || baseType;
};

const displayServiceName = (serviceType = "", namespace = "") => {
  const storedNamespace = String(namespace || "").trim();
  if (
    storedNamespace
    && !INTERNAL_CONFIG_SERVICE_TYPES.has(storedNamespace)
  ) {
    return storedNamespace;
  }
  return cloudwatchNamespaceForServiceType(serviceType);
};

const SERVICE_SEARCH_ALIASES = {
  elasticmapreduce: "aws/elasticmapreduce",
  states: "aws/states",
  stepfunctions: "aws/states",
  mwaa: "aws/mwaa",
  amazonmwaa: "aws/mwaa",
};

function serviceMatchesSearch(service, queryRaw) {
  const query = String(queryRaw || "").toLowerCase().trim();
  if (!query) return true;
  const serviceName = String(service.name || "").toLowerCase();
  const serviceNamespace = String(service.namespace || "").toLowerCase();
  const serviceType = String(service.serviceType || "").toLowerCase();
  if (
    serviceName.includes(query)
    || serviceNamespace.includes(query)
    || serviceType.includes(query)
  ) {
    return true;
  }
  const normalizedQuery = query.replace(/^aws\//, "");
  if (serviceName.replace(/^aws\//, "").includes(normalizedQuery)) return true;
  if (serviceNamespace.replace(/^aws\//, "").includes(normalizedQuery)) return true;
  if (serviceType.replace(/^aws\//, "").includes(normalizedQuery)) return true;
  const alias = SERVICE_SEARCH_ALIASES[normalizedQuery.replace(/\s/g, "")];
  if (alias && (serviceName.includes(alias) || serviceNamespace.includes(alias))) {
    return true;
  }
  if (normalizedQuery === "mwaa" || normalizedQuery === "amazonmwaa") {
    return (
      serviceName.includes("mwaa")
      || serviceNamespace.includes("mwaa")
      || serviceType.includes("mwaa")
    );
  }
  return (service.metrics || []).some((metric) =>
    String(metric?.name || "").toLowerCase().includes(query),
  );
}

const MetricsPage = () => {
  const { user, accounts, dashboardData, setDashboardData } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [namespaceLoading, setNamespaceLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewingService, setViewingService] = useState(null);
  const [form] = Form.useForm();
  const [enableMetricsForm] = Form.useForm();
  const [editingService, setEditingService] = useState(null);
  const [namespaceOptions, setNamespaceOptions] = useState([]);
  const [metricOptions, setMetricOptions] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [categorizedMetrics, setCategorizedMetrics] = useState({});
  const location = useLocation();
  const [accountOptions, setAccountOptions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountType, setSelectedAccountType] = useState("aws"); // 'aws' or 'databricks'
  const [selectedRegion, setSelectedRegion] = useState("us-east-1");
  const [selectedNamespace, setSelectedNamespace] = useState(null);
  const [tags, setTags] = useState([]);
  const [tagKey, setTagKey] = useState("");
  const [tagValue, setTagValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Databricks workspace modal states
  const [databricksWorkspaceModalVisible, setDatabricksWorkspaceModalVisible] =
    useState(false);
  const [databricksWorkspaces, setDatabricksWorkspaces] = useState([]);
  const [selectedDatabricksAccount, setSelectedDatabricksAccount] =
    useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [databricksAccounts, setDatabricksAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState("aws");
  const [dbSearch, setDbSearch] = useState("");
  const [dbPage, setDbPage] = useState(1);
  const dbPageSize = 10;
  // Databricks metrics states
  const [dbClusters, setDbClusters] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [enableMetricsModal, setEnableMetricsModal] = useState(false);
  const [selectedClusters, setSelectedClusters] = useState([]);
  const [viewDbModalVisible, setViewDbModalVisible] = useState(false);
  const [viewingDbCluster, setViewingDbCluster] = useState(null);
  const [editDbModalVisible, setEditDbModalVisible] = useState(false);
  const [editingDbCluster, setEditingDbCluster] = useState(null);
  const [dbEditForm] = Form.useForm();
  const [rawClusters, setRawClusters] = useState([]);
  const [instanceProfiles, setInstanceProfiles] = useState([]);
  const [loadingInstanceProfiles, setLoadingInstanceProfiles] = useState(false);

  // Cost calculation states
  const [costLoading, setCostLoading] = useState(false);
  const [costData, setCostData] = useState(null);
  const [costError, setCostError] = useState(null);
  const [formComplete, setFormComplete] = useState(false);
  const isViewer = !IsEnabled("Metrics");

  // Apply metric filter from URL (?metric=namespace) — from Pipeline Discovery "View Metrics"
  useEffect(() => {
    const metric = searchParams.get("metric");
    const pipeline = searchParams.get("pipeline");
    const pipelineType = (searchParams.get("pipelineType") || "").toLowerCase();
    if (metric && metric.trim()) {
      setSearchText(metric.trim());
    } else if (
      pipeline
      && pipeline.trim()
      && !["airflow", "mwaa"].includes(pipelineType)
    ) {
      setSearchText(pipeline.trim());
    }
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [searchText, selectedAccount]);

  const filteredServices = useMemo(
    () => services.filter((service) => serviceMatchesSearch(service, searchText)),
    [services, searchText],
  );

  const metricsStats = useMemo(() => {
    const source = searchText.trim() ? filteredServices : services;
    return {
      total: source.length,
      active: source.filter((s) => s.status === "ACTIVE").length,
      inactive: source.filter((s) => s.status !== "ACTIVE").length,
    };
  }, [services, filteredServices, searchText]);

  const dbStats = useMemo(() => {
    const total = dbClusters.length;
    const active = dbClusters.filter((c) => {
      const s = String(c.status || "")
        .toLowerCase()
        .trim();
      return s === "enabled" || s === "active";
    }).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [dbClusters]);

  // Create a debounced version of the calculate cost function
  const debouncedCalculateCost = useDebounce((payload) => {
    calculateCost(payload);
  }, 800);

  // Tag handling

  const handleAddTag = () => {
    if (tagKey && tagValue) {
      const newTags = [...tags, { key: tagKey, tag_name: tagValue }];
      setTags(newTags);
      setTagKey("");
      setTagValue("");
    }
  };

  const handleRemoveTag = (index) => {
    const newTags = [...tags];
    newTags.splice(index, 1);
    setTags(newTags);
  };

  const handleTagKeyPress = (e) => {
    if (e.key === "Enter" && tagKey && tagValue) {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Data Fetching & Handling
  // useEffect(() => {
  //   // Check URL for accountId parameter

  //   const params = new URLSearchParams(location.search);
  //   const accountId = params.get('accountId');

  //   if (accountId && accountOptions.length > 0) {
  //     // Check if accountId is in the format databricks_{id} or aws_{id}
  //     const accountFromUrl = accountOptions.find(opt => opt.value === accountId || opt.value === `databricks_${accountId}` || opt.value === `aws_${accountId}`);
  //     if (accountFromUrl) {
  //       if (accountFromUrl.type === 'databricks') {
  //         // const dbAccount = databricksAccounts.find(acc => acc.accountId === accountFromUrl.accountId);
  //         // setSelectedDatabricksAccount(dbAccount);
  //         setSelectedDatabricksAccount(accountFromUrl);
  //         setDatabricksWorkspaceModalVisible(true);  // Show modal
  //         setActiveTab('databricks');
  //       } else {
  //         setSelectedAccount(accountFromUrl.value);
  //       }
  //     }
  //   } else if (accountOptions.length > 0 && !selectedAccount) {
  //     const firstAccount = accountOptions[0].value;
  //     const account = accountOptions.find(opt => opt.value === firstAccount);
  //     if (account && account.type === 'databricks') {
  //       // const dbAccount = databricksAccounts.find(acc => acc.accountId === account.accountId);
  //       // setSelectedDatabricksAccount(dbAccount);
  //       setSelectedDatabricksAccount(account);
  //       setDatabricksWorkspaceModalVisible(true);
  //     } else {
  //       setSelectedAccount(firstAccount);
  //     }
  //   }
  // }, [accountOptions, location.search, selectedAccount, databricksAccounts]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accountId = params.get("accountId");

    if (accountId && accountOptions.length > 0) {
      const accountFromUrl = accountOptions.find(
        (opt) =>
          opt.value === accountId ||
          opt.value === `databricks_${accountId}` ||
          opt.value === `aws_${accountId}`,
      );
      if (accountFromUrl) {
        setSelectedDatabricksAccount(accountFromUrl.type === "databricks" ? accountFromUrl : null);
        setSelectedAccount(accountFromUrl.value);
        setSelectedAccountType(accountFromUrl.type);
        setActiveTab(accountFromUrl.type);
        if (accountFromUrl.type === "databricks") {
          setTimeout(() => fetchDbClusters(accountFromUrl.accountId), 100);
        }
      }
    } else if (accountOptions.length > 0) {
      // Only set default on initial load - use functional updates to avoid overwriting user's tab/account choice
      setSelectedAccount((prev) => {
        if (prev) return prev;
        return accountOptions[0].value;
      });
      setSelectedAccountType((prev) => {
        if (prev) return prev;
        return accountOptions[0].type;
      });
      setActiveTab((prev) => {
        if (prev) return prev;
        return accountOptions[0].type;
      });
      setSelectedDatabricksAccount((prev) => {
        if (prev) return prev;
        const first = accountOptions[0];
        if (first?.type === "databricks") {
          setTimeout(() => fetchDbClusters(first.accountId), 100);
          return first;
        }
        return null;
      });
    }
  }, [accountOptions, location.search]);

  const fetchServices = useCallback(async (accountId) => {
    if (!accountId) return;
    setLoading(true);
    try {
      console.log("[MetricsPage.fetchServices] Fetching services for accountId:", accountId);
      const data = await serviceApi.getServices(accountId);
      console.log("[MetricsPage.fetchServices] Received data:", data);
      const transformedData = data
        .filter((service) => {
          const serviceType = String(service?.type || service?.namespace || "").toLowerCase();
          return serviceType !== "pipeline_lineage" && serviceType !== "pipeline-lineage";
        })
        .map((service) => {
          const isAccountWide = ACCOUNT_WIDE_METRICS_SERVICE_TYPES.has(service.type);
          const pipelineLabel =
            !isAccountWide && service.pipeline_name
              ? ` (${service.pipeline_name})`
              : "";
          const rowKey =
            !isAccountWide && service.pipeline_id
              ? `${service.type}|${service.pipeline_id}`
              : service.type;
          const displayType = displayServiceName(service.type, service.namespace);
          return {
            key: rowKey,
            name: `${displayType}${pipelineLabel}`,
            displayName: displayType,
            serviceType: service.type,
            namespace: displayType,
            pipeline_id: service.pipeline_id || "",
            pipeline_name: service.pipeline_name || "",
            status:
              service.metrics && service.metrics.length > 0 ? "ACTIVE" : "INACTIVE",
            metrics: service.metrics || [],
            regions: service.regions || [],
            period: service.period,
            length: service.length,
            tags: service.searchTags || [],
          };
        });
      console.log("[MetricsPage.fetchServices] Transformed data:", transformedData);
      setServices(transformedData);
    } catch (error) {
      console.error("[MetricsPage.fetchServices] Error fetching services:", error);
      const errorMessage = error?.message || error?.detail || "Failed to fetch metrics";
      console.error("[MetricsPage.fetchServices] Error details:", errorMessage);
      message.error(`Failed to fetch metrics: ${errorMessage}`);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNamespaces = useCallback(async (accountId, region) => {
    if (!accountId || !region) return;
    const awsNumeric = String(accountId).replace(/^aws_/, "");
    setNamespaceLoading(true);
    try {
        const cfgNamespaces = await serviceApi.getNamespaces(awsNumeric, region);
        const options = (Array.isArray(cfgNamespaces) ? cfgNamespaces : [])
          .map((item) => {
            const ns = item?.namespace;
            if (!ns) return null;
            return { value: ns, label: ns };
          })
          .filter(Boolean)
          .sort((a, b) => String(a.label).localeCompare(String(b.label)));
        setNamespaceOptions(options);
    } catch (error) {
      console.error("Error fetching namespaces:", error);
      message.error("Failed to fetch namespaces");
      setNamespaceOptions([]);
    } finally {
      setNamespaceLoading(false);
    }
  }, []);

  // Fetch services when selectedAccount changes
  useEffect(() => {
    if (selectedAccount) {
      // Only fetch AWS services for AWS accounts
      if (selectedAccount.startsWith("aws_")) {
        const awsAccountId = selectedAccount.replace("aws_", "");
        console.log("[MetricsPage.useEffect] Fetching services for account:", awsAccountId);
        fetchServices(awsAccountId);
        fetchNamespaces(awsAccountId, selectedRegion);
      } else if (selectedAccount.startsWith("databricks_")) {
        // For Databricks accounts, we might need different handling
        // For now, just clear services
        setServices([]);
        setNamespaceOptions([]);
      }
    } else {
      setServices([]);
      setNamespaceOptions([]);
    }
  }, [selectedAccount, selectedRegion, fetchServices, fetchNamespaces]);

  const handleAccountChange = (value) => {
    const account = accountOptions.find((opt) => opt.value === value);
    if (account) {
      setSelectedAccountType(account.type);
      setSelectedAccount(value);
      try {
        if (account.type === "aws" && account.accountId) {
          localStorage.setItem(
            "clairai_selected_aws_account_id",
            String(account.accountId).replace(/^aws_/, ""),
          );
        } else {
          localStorage.removeItem("clairai_selected_aws_account_id");
          localStorage.removeItem("selectedAwsAccountId");
        }
      } catch {
        /* ignore */
      }
      if (account.type === "databricks") {
        setActiveTab("databricks");
        const dbAccount = databricksAccounts.find(
          (acc) => acc.accountId === account.accountId,
        );
        setSelectedDatabricksAccount(dbAccount);
        setSelectedAccount(value);
        setTimeout(() => {
          fetchDbClusters(account.accountId);
        }, 100);
      } else {
        // For AWS accounts, set selectedAccount immediately
        setSelectedAccount(value);
        setActiveTab("aws");
      }
    }
  };

  const handleWorkspaceModalCancel = () => {
    setDatabricksWorkspaceModalVisible(false);
    setSelectedDatabricksAccount(null);
    setDatabricksWorkspaces([]);
    // Reset account dropdown to previous selection or first AWS account
    const awsAccount = accountOptions.find((opt) => opt.type === "aws");
    if (awsAccount) {
      setSelectedAccount(awsAccount.value);
    } else {
      setSelectedAccount(null);
    }
  };

  // Handle workspace selection for Databricks accounts
  const handleWorkspaceSelect = async (workspaceId) => {
    if (!selectedDatabricksAccount) return;

    try {
      const clientId = selectedDatabricksAccount.clientId;

      if (!clientId) {
        message.error(
          "Account information not found. Please refresh the page.",
        );
        return;
      }

      // ADD THIS LINE:
      setSelectedAccount(`databricks_${selectedDatabricksAccount.accountId}`);

      setDatabricksWorkspaceModalVisible(false);
      setSelectedWorkspace(workspaceId);
      message.success("Workspace selected successfully");

      setTimeout(() => {
        fetchClustersForWorkspace(workspaceId);
      }, 100);
    } catch (error) {
      console.error("Error selecting workspace:", error);
      message.error("Failed to select workspace");
    }
  };

  // Fetch workspaces when Databricks account is selected
  const fetchDatabricksWorkspaces = useCallback(async () => {
    if (!selectedDatabricksAccount) {
      console.log("No Databricks account selected");
      return;
    }
    console.log("Fetching workspaces for:", selectedDatabricksAccount);
    setWorkspaceLoading(true);
    try {
      // Get client_id directly from backend API response
      const clientId = selectedDatabricksAccount.clientId;
      console.log("Client ID:", clientId);
      if (!clientId) {
        console.error(
          "No clientId found in account:",
          selectedDatabricksAccount,
        );
        message.error(
          "Account information not found. Please refresh the page.",
        );
        setDatabricksWorkspaces([]);
        return;
      }
      console.log(
        "Calling API with accountId:",
        selectedDatabricksAccount.accountId,
      );
      // Get stored credentials for PAT token only
      const storedCreds = getStoredCredentials(
        selectedDatabricksAccount.accountId,
      );

      // Fetch workspaces using account_id (backend uses stored credentials)
      const workspacesData = await databricksService.getWorkspaces(
        selectedDatabricksAccount.accountId,
      );
      console.log("Workspaces received:", workspacesData);
      setDatabricksWorkspaces(workspacesData || []);
    } catch (error) {
      console.error("Failed to fetch workspaces - Full error:", error);
      console.error("Error message:", error.message);
      console.error("Error response:", error.response?.data);
      message.error(
        "Unable to fetch workspaces: " + (error.message || "Unknown error"),
      );
      setDatabricksWorkspaces([]);
    } finally {
      setWorkspaceLoading(false);
    }
  }, [selectedDatabricksAccount]);

  useEffect(() => {
    if (databricksWorkspaceModalVisible && selectedDatabricksAccount) {
      fetchDatabricksWorkspaces();
    }
  }, [
    databricksWorkspaceModalVisible,
    selectedDatabricksAccount,
    fetchDatabricksWorkspaces,
  ]);

  const handleRegionChange = (value) => {
    setSelectedRegion(value);
    // Reset form fields
    form.setFieldsValue({
      type: undefined,
      metrics: [],
    });

    // Fetch namespaces for the new region (numeric account id for service routes)
    if (selectedAccount) {
      const awsNumeric = selectedAccount.replace(/^aws_/, "");
      fetchNamespaces(awsNumeric, value);
    }
  };

  const handleNamespaceChange = (value) => {
    const ns = typeof value === "string" ? value.trim() : "";
    if (!ns) return;
    setSelectedNamespace(ns);
    form.setFieldsValue({ type: ns });
    // Reset metrics when namespace changes
    form.setFieldsValue({ metrics: [] });

    // Fetch metrics for the selected namespace
    if (selectedAccount && selectedRegion) {
      fetchMetrics(selectedAccount, selectedRegion, ns);
    }
  };

  const fetchMetrics = async (accountId, region, namespace) => {
    if (!accountId || !region || !namespace) return;
    const awsNumeric = String(accountId).replace(/^aws_/, "");
    const cwNamespace = cloudwatchNamespaceForServiceType(namespace);
    setMetricsLoading(true);
    try {
      // Get the full response with both metrics and recommended_metrics in one call
      const fullResponse = await serviceApi.getMetricsWithRecommendations(
        awsNumeric,
        region,
        cwNamespace,
      );

      const metricsWithMetadata = buildMetricsWithMetadata(fullResponse);

      // Process metrics data and categorize them
      setMetricOptions(metricsWithMetadata);
      setCategorizedMetrics(categorizeMetrics(metricsWithMetadata));
    } catch (error) {
      console.error("Error fetching metrics:", error);
      message.error("Failed to fetch metrics");
      setMetricOptions([]);
      setCategorizedMetrics({});
    } finally {
      setMetricsLoading(false);
    }
  };

  const buildMetricsWithMetadata = (fullResponse = {}) => {
    const rawMetrics = fullResponse.metrics || [];
    const recommendedMetrics = fullResponse.recommended_metrics || [];
    const normalizeName = (name = "") => String(name).trim().toLowerCase();
    const defaultStats = ["Average", "Minimum", "Maximum", "Sum", "SampleCount"];

    const recommendedByName = new Map(
      recommendedMetrics
        .map((r) => {
          const name = r?.name || r?.metricName || null;
          return name ? [normalizeName(name), { ...r, name }] : null;
        })
        .filter(Boolean),
    );

    const metricsMap = new Map();

    rawMetrics.forEach((metric) => {
      const metricName =
        typeof metric === "string"
          ? metric
          : metric?.name || metric?.metricName || null;
      if (!metricName) return;

      const normalized = normalizeName(metricName);
      const recommended = recommendedByName.get(normalized);
      metricsMap.set(normalized, {
        ...(typeof metric === "object" && metric ? metric : {}),
        name: metricName,
        category: recommended?.category || getCategoryForMetric(metricName),
        display_name: recommended?.display_name || metricName,
        description: recommended?.description || null,
        priority: recommended?.priority || 999,
        isRecommended: !!recommended,
        statistics:
          (typeof metric === "object" && metric?.statistics?.length
            ? metric.statistics
            : defaultStats),
      });
    });

    // Ensure Smart View shows recommendations even if API omits them in `metrics`.
    recommendedMetrics.forEach((rec) => {
      const recName = rec?.name || rec?.metricName || null;
      if (!recName) return;
      const normalized = normalizeName(recName);

      if (!metricsMap.has(normalized)) {
        metricsMap.set(normalized, {
          name: recName,
          category: rec?.category || getCategoryForMetric(recName),
          display_name: rec?.display_name || recName,
          description: rec?.description || null,
          priority: rec?.priority || 999,
          isRecommended: true,
          statistics: defaultStats,
        });
      } else {
        const existing = metricsMap.get(normalized);
        metricsMap.set(normalized, {
          ...existing,
          category: rec?.category || existing.category,
          display_name: rec?.display_name || existing.display_name,
          description: rec?.description || existing.description,
          priority: rec?.priority || existing.priority || 999,
          isRecommended: true,
        });
      }
    });

    return Array.from(metricsMap.values());
  };

  // Helper function to get stored credentials from localStorage
  const getStoredCredentials = (accountId) => {
    try {
      const stored = localStorage.getItem(
        `databricks_credentials_${accountId}`,
      );
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return null;
    }
  };

  const fetchAccounts = async () => {
    if (!user?.client_id) return;

    try {
      // Fetch AWS accounts
      const allAccounts = await accountService.getAccounts();
      const awsOptions = [];

      // Extract AWS accounts from all clients
      for (const clientId in allAccounts) {
        Object.entries(allAccounts[clientId] || {}).forEach(
          ([accountId, details]) => {
            awsOptions.push({
              value: `aws_${accountId}`,
              label: `${details.aws_account_name} (${accountId})`,
              type: "aws",
              accountId: accountId,
              clientId: clientId,
            });
          },
        );
      }

      // Fetch Databricks accounts
      let databricksOptions = [];
      try {
        const databricksAccountsList = await databricksService.getAccounts();
        const accountsWithDetails = databricksAccountsList.map((account) => {
          const accountId = account.account_id;
          const accountName = account.account_name || `Databricks Account ${accountId}`;
          return {
            value: `databricks_${accountId}`,
            label: accountName,  // Display account name instead of account ID
            type: "databricks",
            accountId: accountId,
            accountName: accountName,
            clientId: account.client_id,
            accountUrl:
              account.account_url || "https://accounts.cloud.databricks.com",
          };
        });
        databricksOptions = accountsWithDetails;
        setDatabricksAccounts(accountsWithDetails);
      } catch (error) {
        console.error("Error fetching Databricks accounts:", error);
      }

      const allOptions = [...awsOptions, ...databricksOptions];
      setAccountOptions(allOptions);
    } catch (err) {
      console.error("Error fetching accounts:", err);
      message.error("Failed to fetch accounts");
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [user]);

  // Form field change handler
  const handleFormValuesChange = async (changedValues, allValues) => {
    const account = allValues.account;
    const region = allValues.region;
    const serviceType = allValues.type;
    const metrics = allValues.metrics || [];
    const period = allValues.period;
    const length = allValues.length;

    // Check if all required fields are filled
    const isComplete =
      account &&
      region &&
      serviceType &&
      metrics.length > 0 &&
      metrics.every((m) => m.statistics?.length > 0) &&
      period &&
      length;

    setFormComplete(isComplete);

    // If form is complete, calculate cost
    if (isComplete) {
      // Create a request payload for cost calculation
      // Cost calculation doesn't require role ARN - it only uses metrics, period, and length
      const payload = {
        type: serviceType,
        regions: [region],
        period: parseInt(period) || DEFAULT_SCRAPE_PERIOD,
        length: parseInt(length) || DEFAULT_SCRAPE_LENGTH,
        metrics: metrics,
        roles: [], // Not used by cost calculation endpoint
        searchTags: tags,
      };

      // Use debounced calculation for fields that might change frequently
      if (
        changedValues.period !== undefined ||
        changedValues.length !== undefined ||
        changedValues.metrics !== undefined
      ) {
        debouncedCalculateCost(payload);
      } else {
        calculateCost(payload);
      }
    } else {
      // Reset cost data if form is not complete
      setCostData(null);
    }
  };

  const ensureMetricsLoaded = useCallback(() => {
    const ns = String(form.getFieldValue("type") || "").trim();
    if (!ns) {
      message.warning("Please select a Service Type first");
      return false;
    }
    if (
      selectedAccount &&
      selectedRegion &&
      !metricsLoading &&
      (metricOptions.length === 0 || ns !== selectedNamespace)
    ) {
      fetchMetrics(selectedAccount, selectedRegion, ns);
      setSelectedNamespace(ns);
    }
    return true;
  }, [
    form,
    selectedAccount,
    selectedRegion,
    metricsLoading,
    metricOptions.length,
    selectedNamespace,
  ]);

  const handleMetricsSaved = useCallback(
    (metricsToSave) => {
      const formValues = form.getFieldsValue();
      if (
        formValues.account &&
        formValues.region &&
        formValues.type &&
        metricsToSave.length > 0 &&
        formValues.period &&
        formValues.length
      ) {
        handleFormValuesChange(
          { metrics: metricsToSave },
          {
            ...formValues,
            metrics: metricsToSave,
          },
        );
      }
    },
    [form, handleFormValuesChange],
  );

  // Calculate cost function
  const calculateCost = async (serviceData) => {
    setCostLoading(true);
    setCostError(null);

    try {
      const costingData = await serviceApi.getActiveCosting(serviceData);
      setCostData(costingData);
    } catch (error) {
      console.error("Error calculating cost:", error);
      setCostError(error.message || "Failed to calculate cost");
    } finally {
      setCostLoading(false);
    }
  };

  // Modal/Form Handlers

  const handleEditService = async (service) => {
    const serviceType = service.serviceType || service.type;
    setEditingService(service);
    setTags(service.tags || []);

    // Get the service's region
    const serviceRegion = service.regions[0] || selectedRegion;
    const cwNamespace =
      service.namespace || cloudwatchNamespaceForServiceType(serviceType);
    setSelectedNamespace(serviceType);

    // Set initial form values (type = API service type, not display label)
    form.setFieldsValue({
      account: selectedAccount,
      region: serviceRegion,
      type: serviceType,
      metrics: service.metrics,
      period: service.period,
      length: service.length,
    });

    // Open modal first to show loading state
    setModalVisible(true);

    // Fetch metrics for this namespace when editing
    if (selectedAccount && serviceRegion && serviceType) {
      try {
        setMetricsLoading(true);

        // Use same response model as onboarding so Smart View has recommendation metadata.
        const awsNumeric = selectedAccount.replace(/^aws_/, "");
        const fullResponse = await serviceApi.getMetricsWithRecommendations(
          awsNumeric,
          serviceRegion,
          cwNamespace,
        );
        const metricsWithMetadata = buildMetricsWithMetadata(fullResponse);

        // Update state with fetched metrics
        setMetricOptions(metricsWithMetadata);
        setCategorizedMetrics(categorizeMetrics(metricsWithMetadata));
      } catch (error) {
        console.error("Failed to load metrics for editing:", error);
        message.error("Could not load metrics for this service");
      } finally {
        setMetricsLoading(false);
      }
    }
  };

  const handleUpdateService = async (formValues) => {
    if (!editingService) return;

    setLoading(true);
    try {
      const payload = {
        regions: [formValues.region],
        period: parseInt(formValues.period) || DEFAULT_SCRAPE_PERIOD,
        length: parseInt(formValues.length) || DEFAULT_SCRAPE_LENGTH,
        metrics: formValues.metrics,
        searchTags: tags,
      };

      await serviceApi.updateService(editingService.serviceType || editingService.type, payload);
      message.success("Metrics updated successfully");
      setModalVisible(false);
      const awsAccountId = selectedAccount.replace("aws_", "");
      fetchServices(awsAccountId);

      // Reset state
      setEditingService(null);
      setCategorizedMetrics({});
      setTags([]);
      form.resetFields();
    } catch (error) {
      message.error(
        "Failed to update metrics: " + (error.message || "Unknown error"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateService = async (values) => {
    try {
      const numericAccountId = String(values.account || selectedAccount || "")
        .replace(/^aws_/, "")
        .trim();
      const accountDetails =
        accountOptions.find(
          (opt) =>
            opt.value === values.account ||
            opt.value === selectedAccount ||
            opt.accountId === numericAccountId,
        ) ||
        accounts.find(
          (acc) =>
            acc.accountId === numericAccountId ||
            acc.accountId === values.account ||
            acc.accountId === selectedAccount,
        );
      const role = accountDetails?.arn || accountDetails?.roleArn;

      const roleArn =
        role || `arn:aws:iam::${numericAccountId}:role/ObservabilityRole`;

      const payload = {
        type: values.type,

        regions: [values.region],
        period: parseInt(values.period) || DEFAULT_SCRAPE_PERIOD,
        length: parseInt(values.length) || DEFAULT_SCRAPE_LENGTH,
        metrics: values.metrics,
        roles: [roleArn],
        searchTags: tags,
      };

      setLoading(true);
      await serviceApi.createService(payload);
      setLoading(false);

      message.success("Metrics created successfully");
      setModalVisible(false);
      if (numericAccountId) {
        await fetchServices(numericAccountId);
      }

      // Reset state
      setCategorizedMetrics({});
      setTags([]);
      form.resetFields();
    } catch (error) {
      setLoading(false);
      const friendlyMessage = error?.message || "Unknown error";
      message.error(
        friendlyMessage.includes("already onboarded")
          ? friendlyMessage
          : `Failed to create metrics: ${friendlyMessage}`,
      );
    }
  };

  const handleDeleteService = async (serviceType) => {
    try {
      setLoading(true);
      const awsAccountId = selectedAccount?.replace("aws_", "");
      await serviceApi.deleteService(serviceType, awsAccountId);
      setLoading(false);

      message.success("Metrics deleted successfully");
      if (awsAccountId) {
        fetchServices(awsAccountId);
      }
    } catch (error) {
      setLoading(false);
      message.error(
        "Failed to delete metrics: " + (error.message || "Unknown error"),
      );
    }
  };

  const fetchDbClusters = async (accountIdOverride = null) => {
    setLoading(true);
    try {
      // Use override first, then try to get from state
      const accountId =
        accountIdOverride ||
        selectedDatabricksAccount?.accountId ||
        selectedAccount?.replace("databricks_", "");

      console.log("Fetching clusters for accountId:", accountId); // DEBUG

      if (!accountId) {
        console.log("No accountId found"); // DEBUG
        setDbClusters([]);
        return;
      }

      const allMonitoredClusters =
        await databricksService.getMonitoredMetrics(accountId);
      console.log("Fetched clusters:", allMonitoredClusters); // DEBUG
      
      // Log deployment_name for each cluster to help debug
      if (allMonitoredClusters && allMonitoredClusters.length > 0) {
        allMonitoredClusters.forEach(cluster => {
          console.log(`[fetchDbClusters] Cluster ${cluster.cluster_id}:`, {
            deployment_name: cluster.deployment_name,
            deployment_name_type: typeof cluster.deployment_name,
            workspace_id: cluster.workspace_id
          });
        });
      }

      setDbClusters(allMonitoredClusters || []);
    } catch (error) {
      console.error("Error fetching clusters:", error);
      message.error("Failed to fetch clusters");
      setDbClusters([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle workspace selection change
  const handleWorkspaceChange = (workspaceId) => {
    setSelectedWorkspace(workspaceId);
    // Fetch clusters for the selected workspace
    if (workspaceId) {
      fetchDbClusters();
    }
  };

  // Fetch clusters for a specific workspace
  const fetchClustersForWorkspace = async (workspaceId) => {
    if (!workspaceId) {
      console.log("No workspace ID provided");
      return;
    }

    setLoading(true);
    try {
      const accountId = selectedDatabricksAccount?.accountId;

      console.log("Fetching clusters for:", { accountId, workspaceId });

      if (!accountId) {
        throw new Error("No Databricks account selected");
      }

      // Use getMonitoredMetrics which returns monitored clusters
      const clusters = await databricksService.getMonitoredMetrics(accountId);
      console.log("Raw API response:", clusters);

      // Fetch all monitored clusters for the account
      const allMonitoredClusters =
        await databricksService.getMonitoredMetrics(accountId);

      // // Filter clusters by workspace if needed
      // const filteredClusters = workspaceId
      //     ? allMonitoredClusters.filter(c => c.workspace_id === workspaceId)
      //     : allMonitoredClusters;
      // setDbClusters(filteredClusters || []);
      setDbClusters(allMonitoredClusters || []);
    } catch (error) {
      console.error("Error fetching clusters:", error);
      setDbClusters([]);
    } finally {
      setLoading(false);
    }
  };
  // View cluster details
  const handleViewCluster = (cluster) => {
    setViewingDbCluster(cluster);
    setViewDbModalVisible(true);
  };

  // Edit cluster metrics
  const handleEditCluster = (cluster) => {
    setEditingDbCluster(cluster);
    dbEditForm.setFieldsValue({
      cluster_name: cluster.cluster_name,
      workspace_id: cluster.workspace_id,
      deployment_name: cluster.deployment_name,
      pat_token: cluster.pat_token,
    });
    setEditDbModalVisible(true);
  };

  // Delete cluster metrics
  const handleDeleteCluster = async (clusterId) => {
    try {
      setLoading(true);
      const accountId =
        selectedDatabricksAccount?.accountId ||
        selectedAccount?.replace("databricks_", "");
      const cluster = dbClusters.find((c) => c.cluster_id === clusterId);

      if (!cluster) {
        throw new Error("Cluster not found");
      }

      // Get deployment_name - try from cluster, or fetch from workspace if missing
      let deploymentName = cluster.deployment_name;
      
      // Normalize the value - handle null, undefined, and string 'null'/'undefined'
      const normalizedDeploymentName = deploymentName && typeof deploymentName === 'string' 
        ? deploymentName.trim() 
        : null;
      
      // Check if deployment_name is valid
      if (!normalizedDeploymentName || 
          normalizedDeploymentName === 'undefined' || 
          normalizedDeploymentName === 'null' || 
          normalizedDeploymentName === '') {
        console.log('[Delete Cluster] deployment_name missing or invalid:', {
          original: deploymentName,
          type: typeof deploymentName,
          cluster: cluster.cluster_id,
          workspace_id: cluster.workspace_id
        });
        
        try {
          if (!accountId) {
            throw new Error('Account ID is required to fetch deployment_name');
          }
          
          console.log('[Delete Cluster] Fetching workspaces for account:', accountId);
          const workspaces = await databricksService.getMetricsWorkspaces(accountId);
          console.log('[Delete Cluster] Fetched workspaces:', workspaces);
          
          const workspace = workspaces.find(w => w.workspace_id === cluster.workspace_id);
          console.log('[Delete Cluster] Found workspace:', workspace);
          
          if (workspace) {
            // Try to get deployment_name from workspace object
            if (workspace.deployment_name) {
              deploymentName = workspace.deployment_name.trim();
              console.log('[Delete Cluster] Found deployment_name from workspace:', deploymentName);
            } 
            // If deployment_name is missing, try to extract from workspace_url
            else if (workspace.workspace_url) {
              const urlParts = workspace.workspace_url.split('//');
              if (urlParts.length > 1) {
                deploymentName = urlParts[1].split('.')[0];
                console.log('[Delete Cluster] Extracted deployment_name from workspace_url:', deploymentName);
              }
            }
            
            // If still no deployment_name, check if cluster has workspace_name that we can use
            if (!deploymentName && cluster.workspace_name) {
              // Sometimes workspace_name might be the deployment name
              deploymentName = cluster.workspace_name.trim();
              console.log('[Delete Cluster] Using workspace_name as deployment_name:', deploymentName);
            }
            
            if (!deploymentName || deploymentName === 'null' || deploymentName === 'undefined' || deploymentName.trim() === '') {
              throw new Error(`deployment_name not found for workspace ${cluster.workspace_id}. Workspace data: ${JSON.stringify(workspace)}`);
            }
          } else {
            throw new Error(`Workspace ${cluster.workspace_id} not found. Available workspaces: ${workspaces.map(w => w.workspace_id).join(', ')}`);
          }
        } catch (error) {
          console.error('[Delete Cluster] Error fetching deployment_name:', error);
          message.error(`deployment_name is required but not available. Error: ${error.message}. Please refresh the page and try again.`);
          throw new Error(`deployment_name is required but not available: ${error.message}`);
        }
      } else {
        deploymentName = normalizedDeploymentName;
      }
      
      // Final validation before API call
      if (!deploymentName || deploymentName === 'null' || deploymentName === 'undefined' || deploymentName.trim() === '') {
        const errorMsg = 'deployment_name is required but not available. Please refresh the page and try again.';
        console.error('[Delete Cluster] Final validation failed:', { deploymentName, cluster });
        message.error(errorMsg);
        throw new Error(errorMsg);
      }

      // Use toggleMetrics to disable the cluster
      const accountName = selectedDatabricksAccount?.accountName;
      await databricksService.toggleMetrics(
        cluster.workspace_id,
        deploymentName,
        clusterId,
        false, // disable
        accountId,  // Pass account_id as fallback
        accountName,  // Prefer account_name for OAuth token generation
        cluster.pat_token || null,  // PAT token optional - backend will use OAuth if not provided
        null   // instance_profile_arn not needed when disabling
      );

      message.success("Cluster metrics disabled successfully");
      fetchDbClusters();
    } catch (error) {
      console.error("Error disabling cluster:", error);
      message.error("Failed to disable cluster metrics");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Raw clusters (not yet enabled) from Databricks
  const fetchRawClusters = async (workspaceId, deploymentName) => {
    if (!workspaceId || !deploymentName) {
      message.error("Missing required parameters: workspace ID and deployment name");
      return;
    }
    
    // Get account_name from selected account for OAuth token (prefer account_name over account_id)
    const accountName = selectedDatabricksAccount?.accountName;
    const accountId = selectedDatabricksAccount?.accountId;
    
    // Validate: account_name or account_id must be provided (OAuth is required)
    if (!accountName && !accountId) {
      message.error("Account must be selected to use OAuth authentication");
      return;
    }
    
    setLoading(true);
    setLoadingInstanceProfiles(true);
    try {
      // Fetch clusters and instance profiles in parallel
      const [clustersResponse, instanceProfilesData] = await Promise.all([
        databricksService.getMetricsClusters(
          workspaceId,
          deploymentName,
          accountId,  // Pass account_id as fallback
          accountName  // Prefer account_name for OAuth token generation
        ).catch(err => {
          console.error('[fetchRawClusters] ERROR fetching clusters:', err);
          throw err;
        }),
        databricksService.getInstanceProfiles(
          deploymentName,
          accountId,  // Pass account_id as fallback
          accountName  // Prefer account_name for OAuth token generation
        ).catch(err => {
          console.error('[fetchRawClusters] ERROR fetching instance profiles:', err);
          // Don't show warning if it's a 404 - might just mean no profiles configured
          if (err.message && !err.message.includes('404')) {
            message.warning('Failed to fetch instance profiles from workspace API. Will try to extract from clusters.');
          }
          return [];
        })
      ]);
      
      // Extract clusters from response
      const clusters = clustersResponse?.clusters || clustersResponse || [];
      setRawClusters(clusters);
      
      // Extract instance profiles from clusters
      const instanceProfilesFromClusters = new Map();
      clusters.forEach(cluster => {
        if (cluster.instance_profile_arn) {
          const arn = cluster.instance_profile_arn;
          if (!instanceProfilesFromClusters.has(arn)) {
            instanceProfilesFromClusters.set(arn, {
              arn: arn,
              name: arn.split('/').pop() || arn
            });
          }
        }
      });
      
      // Merge instance profiles from API and clusters
      const allInstanceProfiles = new Map();
      
      // Add profiles from API
      (instanceProfilesData || []).forEach(profile => {
        if (profile.arn) {
          allInstanceProfiles.set(profile.arn, profile);
        }
      });
      
      // Add profiles from clusters (will override API profiles if duplicate)
      instanceProfilesFromClusters.forEach((profile, arn) => {
        allInstanceProfiles.set(arn, profile);
      });
      
      const mergedProfiles = Array.from(allInstanceProfiles.values());
      console.log(`[fetchRawClusters] Found ${mergedProfiles.length} instance profiles (${instanceProfilesData?.length || 0} from API, ${instanceProfilesFromClusters.size} from clusters)`);
      setInstanceProfiles(mergedProfiles);
    } catch (error) {
      console.error("Error fetching raw clusters:", error);
      message.error("Failed to fetch clusters");
      setRawClusters([]);
    } finally {
      setLoading(false);
      setLoadingInstanceProfiles(false);
    }
  };

  // MetricsSelector Component
  // const MetricsSelector = ({ value = [], onChange }) => {
  //   console.log('value', value)
  //   const [searchText, setSearchText] = useState("");
  //   const [modalVisible, setModalVisible] = useState(false);
  //   const [tempSelectedMetrics, setTempSelectedMetrics] = useState([]);

  //   // Initialize temporary metrics when modal opens
  //   useEffect(() => {
  //     if (modalVisible) {
  //       setTempSelectedMetrics([...value]);
  //     }
  //   }, [modalVisible, value]);

  //   // Fetch Databricks workspaces when account changes to Databricks
  // useEffect(() => {
  //   if (selectedAccount && selectedAccount.startsWith('databricks_')) {
  //     const accountId = selectedAccount.replace('databricks_', '');
  //     fetchDatabricksWorkspacesForMetrics(accountId);
  //   } else {
  //     setWorkspaceOptions([]);
  //     setSelectedWorkspace(null);
  //     setDbClusters([]);
  //   }
  // }, [selectedAccount]);

  // useEffect(() => {
  //   if (!workspaceOptions.length) return;

  //   // If no workspace selected yet, pick the first one
  //   if (!selectedWorkspace) {
  //     const first = workspaceOptions[0].value;
  //     setSelectedWorkspace(first);
  //     fetchDbClusters(first);
  //   }
  // }, [workspaceOptions]);

  // // Fetch workspaces for Databricks metrics
  // const fetchDatabricksWorkspacesForMetrics = async (accountId) => {
  //   try {
  //     const workspacesData = await databricksService.getWorkspaces(accountId);
  //     const options = workspacesData.map(ws => ({
  //       value: ws.workspace_id,
  //       label: ws.workspace_name || `Workspace ${ws.workspace_id}`
  //     }));
  //     setWorkspaceOptions(options);

  //   if (options.length > 0) {
  //     const firstWorkspace = options[0].value;
  //     setSelectedWorkspace(firstWorkspace);
  //     fetchClustersForWorkspace(firstWorkspace);  // Direct call, no setTimeout
  //   }
  // } catch (error) {
  //   console.error("Error fetching workspaces:", error);
  //   setWorkspaceOptions([]);
  // }
  // };

  //   // When user confirms selection in modal
  //   const handleModalOk = () => {
  //     // Update the form field with the selected metrics
  //     onChange(tempSelectedMetrics);
  //     setModalVisible(false);

  //     // Get current form values to check if we can trigger cost calculation
  //     const formValues = form.getFieldsValue();
  //     if (formValues.account && formValues.region &&
  //         formValues.type && tempSelectedMetrics.length > 0 &&
  //         formValues.period && formValues.length) {
  //       // Trigger cost calculation
  //       handleFormValuesChange({metrics: tempSelectedMetrics}, {
  //         ...formValues,
  //         metrics: tempSelectedMetrics
  //       });
  //     }
  //   };

  //   const handleModalCancel = () => {
  //     setModalVisible(false);
  //   };

  //   const filterMetrics = (metrics, searchText) => {
  //     const search = searchText.toLowerCase().trim();
  //     if (!search) return metrics;
  //     if (search.length < 3) {
  //       return metrics.filter((m) => m.name.toLowerCase().startsWith(search));
  //     }
  //     return metrics.filter((m) => m.name.toLowerCase().includes(search));
  //   };

  //   const handleMetricToggle = (metric, checked) => {
  //     let newSelectedMetrics;
  //     if (checked) {
  //       // Check for duplicates first before adding
  //       if (!tempSelectedMetrics.some(m => m.name === metric.name)) {
  //         // Add metric with default statistics array if available
  //         const availableStats = metricOptions.find(m => m.name === metric.name)?.statistics || [];
  //         newSelectedMetrics = [...tempSelectedMetrics, {
  //           name: metric.name,
  //           statistics: availableStats // Select ALL statistics instead of just the first one

  //         }];
  //       } else {
  //         // Metric already exists, don't add a duplicate
  //         return;
  //       }
  //     } else {
  //       // Remove metric

  //       newSelectedMetrics = tempSelectedMetrics.filter((m) => m.name !== metric.name);
  //     }
  //     setTempSelectedMetrics(newSelectedMetrics);
  //   };

  //   const handleStatisticsChange = (metricName, statName, event) => {
  //     const isChecked= event.target.checked
  //     const newSelectedMetrics = tempSelectedMetrics.map((metric) => {
  //       if (metric.name === metricName) {
  //         const newStats = isChecked
  //           ? [...new Set([...(metric.statistics || []), statName])] // Use Set to avoid duplicate statistics
  //           : (metric.statistics || []).filter((s) => s !== statName);
  //         return { ...metric, statistics: newStats };
  //       }
  //       return metric;
  //     });

  //     setTempSelectedMetrics(newSelectedMetrics);
  //   };

  //   const handleSelectAll = () => {
  //     // Get all metrics from all categories
  //     const allMetrics = Object.values(metricOptions).flat();

  //     // Create a map of existing metrics to preserve settings
  //     const existingMetricsMap = {};

  //     tempSelectedMetrics.forEach(metric => {
  //       existingMetricsMap[metric.name] = metric.statistics || [];
  //     });

  //     // Create selected metrics with ALL statistics selected for each
  //     const allSelectedMetrics = allMetrics.map((metric) => {
  //       // If the metric was already selected, keep its statistics
  //       if (existingMetricsMap[metric.name]) {
  //         return {
  //           name: metric.name,
  //           statistics: existingMetricsMap[metric.name]
  //         };
  //       }

  //       // Otherwise, add it with ALL available statistics selected
  //       const availableStats = metric.statistics || [];
  //       return {
  //         name: metric.name,
  //         statistics: availableStats // Select ALL statistics
  //       };
  //     });

  //     setTempSelectedMetrics(allSelectedMetrics);
  //   };

  //   // Select all metrics in the currently displayed filtered list
  //   const handleSelectAllFiltered = (filteredMetrics) => {
  //     // Get metric names that are currently filtered
  //     const filteredMetricNames = filteredMetrics.map(m => m.name);

  //     // Check if all filtered metrics are already selected
  //     const allSelected = areAllFilteredSelected(filteredMetrics);

  //     if (allSelected) {
  //       // DESELECT: Remove all filtered metrics from selection
  //       const remainingSelections = tempSelectedMetrics.filter(m => !filteredMetricNames.includes(m.name));
  //       setTempSelectedMetrics(remainingSelections);
  //     } else {
  //       // SELECT: Add all filtered metrics to selection

  //       // Keep existing selections that are NOT in the filtered list
  //       const otherSelections = tempSelectedMetrics.filter(m => !filteredMetricNames.includes(m.name));

  //       // Add all filtered metrics with their statistics
  //       const filteredSelections = filteredMetrics.map((metric) => {
  //         const availableStats = metric.statistics || [];
  //         return {
  //           name: metric.name,
  //           statistics: availableStats // Select ALL statistics
  //         };
  //       });

  //       setTempSelectedMetrics([...otherSelections, ...filteredSelections]);
  //     }
  //   };

  //   // Check if all filtered metrics are selected
  //   const areAllFilteredSelected = (filteredMetrics) => {
  //     if (filteredMetrics.length === 0) return false;
  //     return filteredMetrics.every(metric =>
  //       tempSelectedMetrics.some(selected => selected.name === metric.name)
  //     );
  //   };

  //   const isMetricSelected = (metricName) => {
  //     return tempSelectedMetrics.some((m) => m.name === metricName);
  //   };

  //   const isStatisticSelected = (metricName, statName) => {
  //     console.log('statname', statName)
  //     const metric = tempSelectedMetrics.find((m) => m.name === metricName);
  //     return metric?.statistics?.includes(statName) || false;
  //   };

  //   const [viewMode, setViewMode] = useState('smart'); // 'smart' or 'all'
  //   const [selectedCategory, setSelectedCategory] = useState('all'); // 'all', 'performance', 'errors', etc.

  //   const renderModalContent = () => {
  //     // Get all metrics from all categories
  //     const mertricsOpt = metricOptions;
  //     tempSelectedMetrics.forEach(tsm => {
  //       if (!mertricsOpt.find(({ name }) => name === tsm.name)) {
  //         tsm.dimensions = []
  //         mertricsOpt.push(tsm)
  //       }
  //     })
  //     setMetricOptions(mertricsOpt);

  //     const metricCategories = categorizeMetrics(mertricsOpt);
  //     const allMetrics = Object.values(metricCategories).flat();

  //     // Filter metrics based on search text
  //     const filteredMetrics = filterMetrics(allMetrics, searchText);

  //     // Get available statistics for a metric
  //     const getAvailableStats = (metricName) => {
  //       const metric = metricOptions.find(m => m.name === metricName);
  //       return metric?.statistics || [];
  //     };

  //     // Get category for a metric
  //     const getMetricCategory = (metric) => {
  //       return (metric.category || getCategoryForMetric(metric.name)).toLowerCase();
  //     };

  //     // Get unique categories only from recommended metrics
  //     const recommendedMetrics = allMetrics.filter(m => m.isRecommended);
  //     const uniqueCategories = [...new Set(recommendedMetrics.map(m => getMetricCategory(m)))].sort();

  //     // Filter by view mode (smart = recommended only)
  //     const viewFilteredMetrics = viewMode === 'smart'
  //       ? filteredMetrics.filter(m => m.isRecommended)
  //       : filteredMetrics;

  //     // Filter by category
  //     const categoryFilteredMetrics = selectedCategory === 'all'
  //       ? viewFilteredMetrics
  //       : viewFilteredMetrics.filter(m => getMetricCategory(m) === selectedCategory);

  //     if (metricsLoading) {
  //       return (
  //         <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
  //           <Spin />
  //           <div style={{ marginTop: 12 }}>Loading metrics...</div>
  //         </div>
  //       );
  //     }

  //     if (allMetrics.length === 0) {
  //       return (
  //         <Empty
  //           description="No metrics available for this namespace"
  //           image={Empty.PRESENTED_IMAGE_SIMPLE}
  //         />
  //       );
  //     }

  //     return (
  //       <div>
  //         {/* Search Bar */}
  //         <div style={{ marginBottom: 16 }}>
  //           <Input
  //             placeholder="Search metrics..."
  //             value={searchText}
  //             onChange={e => setSearchText(e.target.value)}
  ////             style={{ width: '100%' }}
  //             allowClear
  //           />
  //         </div>

  //         {/* View Mode Tabs */}
  //         <div style={{ marginBottom: 16, display: 'flex', gap: '8px' }}>
  //           <Button
  //             type={viewMode === 'smart' ? 'primary' : 'default'}
  //             onClick={() => setViewMode('smart')}
  //             style={{ borderRadius: '6px' }}
  //           >
  //             Smart View
  //           </Button>
  //           <Button
  //             type={viewMode === 'all' ? 'primary' : 'default'}
  //             onClick={() => setViewMode('all')}
  //             style={{ borderRadius: '6px' }}
  //           >
  //             All Metrics
  //           </Button>
  //         </div>

  //         {/* Category Filter Pills */}
  //         <div style={{ marginBottom: 16, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
  //           {uniqueCategories.map(category => {
  //             const categoryLabel = category
  //               .split('_')
  //               .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  //               .join(' ');

  //             return (
  //               <Button
  //                 key={category}
  //                 type={selectedCategory === category ? 'primary' : 'default'}
  //                 onClick={() => setSelectedCategory(category)}
  //                 style={{
  //                   borderRadius: '20px',
  //                   background: selectedCategory === category ? '#E3F2FD' : '#fff',
  //                   color: selectedCategory === category ? '#1976D2' : '#666',
  //                   border: selectedCategory === category ? '1px solid #1976D2' : '1px solid #d9d9d9'
  //                 }}
  //                 ghost={selectedCategory !== category}
  //               >
  //                 {categoryLabel}
  //               </Button>
  //             );
  //           })}
  //           <Button
  //             type={selectedCategory === 'all' ? 'primary' : 'default'}
  //             onClick={() => setSelectedCategory('all')}
  //             style={{
  //               borderRadius: '20px',
  //               background: selectedCategory === 'all' ? '#E3F2FD' : '#fff',
  //               color: selectedCategory === 'all' ? '#1976D2' : '#666',
  //               border: selectedCategory === 'all' ? '1px solid #1976D2' : '1px solid #d9d9d9'
  //             }}
  //             ghost={selectedCategory !== 'all'}
  //           >
  //             All Categories
  //           </Button>
  //         </div>

  //         {/* Recommended Section Header */}
  //         {viewMode === 'smart' && (
  //           <div style={{
  //             marginBottom: 12,
  //             display: 'flex',
  //             alignItems: 'center',
  //             justifyContent: 'space-between',
  //             paddingBottom: '8px',
  //             borderBottom: '1px solid #f0f0f0'
  //           }}>
  //             <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
  //               <span style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
  //                 Recommended for {form.getFieldValue('type') || 'AWS/AOSS'}
  //               </span>
  //               <Tag color="orange" style={{ margin: 0 }}>Recommended</Tag>
  //             </div>
  //             <Button
  //               type="link"
  //               size="small"
  //               onClick={() => handleSelectAllFiltered(categoryFilteredMetrics)}
  //               style={{ padding: '0 8px', height: 'auto' }}
  //             >
  //               {areAllFilteredSelected(categoryFilteredMetrics) ? 'Clear All' : 'Select All'}
  //             </Button>
  //           </div>
  //         )}

  //         {/* All Metrics Section Header with Select All */}
  //         {viewMode === 'all' && (
  //           <div style={{
  //             marginBottom: 12,
  //             display: 'flex',
  //             alignItems: 'center',
  //             justifyContent: 'space-between',
  //             paddingBottom: '8px',
  //             borderBottom: '1px solid #f0f0f0'
  //           }}>
  //             <span style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
  //               {selectedCategory === 'all'
  //                 ? 'All Metrics'
  //                 : selectedCategory.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  //               }
  //               {categoryFilteredMetrics.length > 0 && (
  //                 <span style={{ fontSize: '13px', fontWeight: 400, color: '#999', marginLeft: '8px' }}>
  //                   ({categoryFilteredMetrics.length})
  //                 </span>
  //               )}
  //             </span>
  //             <Button
  //               type="link"
  //               size="small"
  //               onClick={() => handleSelectAllFiltered(categoryFilteredMetrics)}
  //               disabled={categoryFilteredMetrics.length === 0}
  //               style={{ padding: '0 8px', height: 'auto' }}
  //             >
  //               {areAllFilteredSelected(categoryFilteredMetrics) ? 'Clear All' : 'Select All'}
  //             </Button>
  //           </div>
  //         )}

  //         {/* Metrics List */}
  //         <div style={{
  //           height: 'calc(70vh - 280px)',
  //           overflowY: 'auto',
  //           paddingRight: '8px'
  //         }}>
  //           {categoryFilteredMetrics.map((metric) => {
  //             const selected = isMetricSelected(metric.name);
  //             const availableStats = getAvailableStats(metric.name);
  //             const category = getMetricCategory(metric);

  //             // Category badge color mapping
  //             const categoryColors = {
  //               'performance': '#1976D2',
  //               'error_detection': '#D32F2F',
  //               'errors': '#D32F2F',
  //               'requests': '#7B1FA2',
  //               'business_critical': '#FF6F00',
  //               'resource_utilization': '#388E3C',
  //               'cost_optimization': '#00897B',
  //               'storage': '#388E3C',
  //               'other': '#616161'
  //             };

  //             // Format category label
  //             const categoryLabel = category
  //               .split('_')
  //               .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  //               .join(' ');

  //             return (
  //               <div key={metric.name} style={{
  //                 marginBottom: "16px",
  //                 padding: '12px',
  //                 background: '#fff',
  //                 border: '1px solid #f0f0f0',
  //                 borderRadius: '8px'
  //               }}>
  //                 <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
  //                   <Checkbox
  //                     checked={selected}
  //                     onChange={(e) => handleMetricToggle(metric, e.target.checked)}
  //                     style={{ marginTop: '2px' }}
  //                   />

  //                   <div style={{ flex: 1 }}>
  //                     <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
  //                       <span style={{ fontSize: "15px", fontWeight: 600, color: '#333' }}>
  //                         {metric.display_name || metric.name}
  //                       </span>
  //                       {metric.isRecommended && (
  //                         <Tag
  //                           color="gold"
  //                           style={{
  //                             margin: 0,
  //                             fontSize: '11px',
  //                             padding: '0 6px',
  //                             lineHeight: '18px'
  //                           }}
  //                         >
  //                           ★
  //                         </Tag>
  //                       )}
  //                     </div>

  //                     <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
  //                       {metric.description || `CloudWatch metric for monitoring ${metric.name}`}
  //                     </div>

  //                     <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: selected ? '12px' : '0' }}>
  //                       <Tag
  //                         style={{
  //                           margin: 0,
  //                           background: '#F5F5F5',
  //                           color: categoryColors[category] || '#666',
  //                           border: 'none',
  //                           fontSize: '12px'
  //                         }}
  //                       >
  //                         {categoryLabel}
  //                       </Tag>
  //                     </div>

  //                     {selected && availableStats.length > 0 && (
  //                       <div style={{
  //                         paddingTop: '12px',
  //                         borderTop: '1px solid #f0f0f0'
  //                       }}>
  //                         <div style={{
  //                           marginBottom: "8px",
  //                           color: isDark ? "#adbac7" : "#666",
  //                           fontSize: "13px",
  //                           fontWeight: 500
  //                         }}>
  //                           Statistics:
  //                         </div>
  //                         <Space direction="horizontal" size={8} wrap>
  //                           {availableStats.map((stat) => (
  //                             <Checkbox
  //                               key={stat}
  //                               checked={isStatisticSelected(metric.name, stat)}
  //                               onChange={(e) => handleStatisticsChange(metric.name, stat, e)}
  //                             >
  //                               <span style={{ fontSize: "13px" }}>{stat}</span>
  //                             </Checkbox>
  //                           ))}
  //                         </Space>
  //                       </div>
  //                     )}

  //                     {selected && availableStats.length === 0 && (
  //                       <div style={{
  //                         marginTop: '8px',
  //                         paddingTop: '8px',
  //                         borderTop: '1px solid #f0f0f0',
  //                         color: '#999',
  //                         fontSize: '12px',
  //                         fontStyle: 'italic'
  //                       }}>
  //                         No statistics available
  //                       </div>
  //                     )}
  //                   </div>
  //                 </div>
  //               </div>
  //             );
  //           })}

  //           {categoryFilteredMetrics.length === 0 && (
  //             <Empty
  //               description="No matching metrics found"
  //               image={Empty.PRESENTED_IMAGE_SIMPLE}
  //             />
  //           )}
  //         </div>

  //         {/* Footer */}
  //         <div style={{
  //           marginTop: 16,
  //           paddingTop: 16,
  //           borderTop: '1px solid #f0f0f0',
  //           display: 'flex',
  //           justifyContent: 'space-between',
  //           alignItems: 'center'
  //         }}>
  //           <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
  //             <span style={{ fontSize: '14px', color: '#666' }}>
  //               <strong style={{ color: '#333' }}>{tempSelectedMetrics.length}</strong> metrics selected
  //             </span>
  //             <Button
  //               type="link"
  //               onClick={() => handleSelectAllFiltered(categoryFilteredMetrics)}
  //               style={{ padding: 0, height: 'auto' }}
  //               disabled={categoryFilteredMetrics.length === 0}
  //             >
  //               {areAllFilteredSelected(categoryFilteredMetrics) ? 'Clear All' : 'Select All Visible'}
  //             </Button>
  //             <Button
  //               type="link"
  //               onClick={() => setTempSelectedMetrics([])}
  //               style={{ padding: 0, height: 'auto' }}
  //               danger
  //             >
  //               Clear All Selections
  //             </Button>
  //           </div>

  //           <Space>
  //             <Button onClick={handleModalCancel}>Cancel</Button>
  //             <Button
  //               type="primary"
  //               onClick={handleModalOk}
  //               disabled={tempSelectedMetrics.length === 0 || tempSelectedMetrics.some(m => !m.statistics?.length)}
  //             >
  //               Save Selection
  //             </Button>
  //           </Space>
  //         </div>
  //       </div>
  //     );
  //   };

  //   // Custom display to show selected metrics summary
  //   const renderSelectedMetricsSummary = () => {
  //     if (!value || value.length === 0) {
  //       return <div style={{ color: '#999' }}>No metrics selected</div>;
  //     }

  //     return (
  //       <div style={{
  //         display: 'flex',
  //         flexWrap: 'wrap',
  //         gap: '4px',
  //         maxHeight: '60px',
  //         overflowY: 'auto'
  //       }}>
  //         {value.map((metric) => (
  //           <Tag
  //             key={metric.name}
  //             color="blue"
  //             style={{ marginRight: 0 }}
  //             title={`${metric.name} (${metric.statistics?.join(', ') || 'No statistics'})`}
  //           >
  //             {metric.name}
  //           </Tag>
  //         ))}

  //         {value.length > 0 && (
  //           <div style={{ fontSize: '12px', color: '#666', width: '100%', marginTop: '4px' }}>
  //             {value.length} metrics selected
  //           </div>
  //         )}
  //       </div>
  //     );
  //   };

  //   return (
  //     <div className="metrics-selector">

  //       {/* Custom field that opens the modal when clicked */}
  //       <div
  //         onClick={() => setModalVisible(true)}
  //         style={{
  //           border: '1px solid #d9d9d9',
  //           borderRadius: '2px',
  //           padding: '4px 11px',
  //           minHeight: '32px',
  //           cursor: 'pointer',
  //           background: '#fff',
  //           transition: 'all 0.3s',
  //           position: 'relative'
  //         }}
  //       >
  //         {renderSelectedMetricsSummary()}
  //         <div
  //           style={{
  //             position: 'absolute',
  //             right: '11px',
  //             top: '50%',
  //             transform: 'translateY(-50%)',
  //             color: '#999'
  //           }}
  //         >
  //           
  //         </div>
  //       </div>

  //       {/* Modal for metric selection */}
  //       <Modal
  //         title="Select Metrics"
  //         open={modalVisible}
  //         onCancel={handleModalCancel}
  //         footer={null}
  //         width={800}
  //         destroyOnClose
  //         bodyStyle={{ padding: '16px' }}
  //       >
  //         {renderModalContent()}
  //       </Modal>
  //       </div>
  //   );
  // };

  const columns = [
    {
      title: <span style={{ color: isDark ? "#e6edf3" : "#000" }}>Service Name</span>,
      dataIndex: "name",
      key: "name",
      render: (text) => <strong style={{ color: isDark ? "#e6edf3" : "#000" }}>{text}</strong>,
      width: 180,
      fixed: "left",
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : "#000" }}>Regions</span>,
      dataIndex: "regions",
      key: "regions",
      render: (regions) => (
        <Space wrap>
          {regions?.map((region) => (
            <Tag
              key={region}
              style={{
                background: "#1990FF",
                color: "#fff",
                border: "1px solid #1990FF",
                borderRadius: "8px",
                fontWeight: 500,
              }}
            >
              {region}
            </Tag>
          ))}
        </Space>
      ),
      width: 150,
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : "#000" }}>Status</span>,
      dataIndex: "status",
      key: "status",
      render: (status) =>
        status === "ACTIVE" ? (
          <Tag
            style={{
              background: "#16a34a",
              color: "#fff",
              border: "1px solid #16a34a",
              borderRadius: "8px",
              fontWeight: 500,
              padding: "4px 16px",
            }}
          >
            Active
          </Tag>
        ) : status === "PENDING" ? (
          <Tag
            color="processing"
            style={{
              borderRadius: "16px",
              padding: "4px 12px",
              fontWeight: "500",
              border: "none",
            }}
          >
            Pending
          </Tag>
        ) : (
          <Tag
            color="warning"
            style={{
              borderRadius: "16px",
              padding: "4px 12px",
              fontWeight: "500",
              border: "none",
            }}
          >
            {status ? status.charAt(0) + status.slice(1).toLowerCase() : ""}
          </Tag>
        ),
      width: 100,
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : "#000" }}>Metrics</span>,
      dataIndex: "metrics",
      key: "metrics",
      render: (metrics) => {
        if (!metrics || metrics.length === 0) {
          return <span style={{ color: isDark ? "#8b949e" : "#999" }}>No metrics</span>;
        }

        // Display first 5 metrics and indicate if there are more
        const displayCount = 2;
        const hasMore = metrics.length > displayCount;
        const displayMetrics = hasMore
          ? metrics.slice(0, displayCount)
          : metrics;

        return (
          <div
            style={{
              maxWidth: "100%",
              wordBreak: "break-word",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {displayMetrics.map((metric, index) => (
              <Tag
                color="blue"
                key={`${metric.name}-${index} 'hello'`}
                style={{
                  display: "inline-block",
                  marginBottom: 2,
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={`${metric.name} (${metric.statistics?.join(", ") || "N/A"})`}
              >
                {metric.name} ({metric.statistics?.join(", ") || "N/A"})
              </Tag>
            ))}
            {hasMore && (
              <div style={{ color: isDark ? "#8b949e" : "#999", fontSize: "12px" }}>
                ...and {metrics.length - displayCount} more
              </div>
            )}
          </div>
        );
      },
      width: 300,
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : "#000" }}>Period</span>,
      dataIndex: "period",
      key: "period",
      width: 100,
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : "#000" }}>Length</span>,
      dataIndex: "length",
      key: "length",
      width: 100,
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : "#000" }}>Tags</span>,
      dataIndex: "tags",
      key: "tags",
      render: (tags) => {
        if (!tags || tags.length === 0) {
          return <span style={{ color: isDark ? "#8b949e" : "#000" }}>No tags</span>;
        }

        // Display first 5 tags and indicate if there are more
        const displayCount = 5;
        const hasMore = tags.length > displayCount;
        const displayTags = hasMore ? tags.slice(0, displayCount) : tags;

        return (
          <div
            style={{
              maxWidth: "100%",
              wordBreak: "break-word",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {displayTags.map((tag, index) => (
              <Tag
                color="purple"
                key={`${tag.key}-${tag.tag_name}-${index}`}
                style={{
                  display: "inline-block",
                  marginBottom: 2,
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={`${tag.key}: ${tag.tag_name}`}
              >
                {tag.key}: {tag.tag_name}
              </Tag>
            ))}
            {hasMore && (
              <div style={{ color: "#999", fontSize: "12px" }}>
                ...and {tags.length - displayCount} more
              </div>
            )}
          </div>
        );
      },
      width: 200,
    },
    {
      title: <span style={{ color: isDark ? "#e6edf3" : "#000" }}>Actions</span>,
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View">
            <Button
              icon={<EyeOutlined />}
              type="text"
              size="small"
              onClick={() => {
                setViewingService(record);
                setViewModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              icon={<EditFilled />}
              type="text"
              size="small"
              onClick={() => handleEditService(record)}
              disabled={isViewer}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Metrics"
            description="Are you sure you want to delete these metrics?"
            onConfirm={() => handleDeleteService(record.serviceType || record.name)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                icon={<DeleteFilled />}
                type="text"
                danger
                size="small"
                disabled={isViewer}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
      fixed: "right",
    },
  ];

  // Region options
  const regionOptions = [
    { value: "us-east-1", label: "US East (N. Virginia)" },
    { value: "us-east-2", label: "US East (Ohio)" },
    { value: "us-west-1", label: "US West (N. California)" },
    { value: "us-west-2", label: "US West (Oregon)" },
    { value: "eu-west-1", label: "EU (Ireland)" },
    { value: "eu-west-2", label: "EU (London)" },
  ];

  // Calculate count for Databricks accounts (used in Empty description)
  const databricksAccountCount = databricksAccounts.length;

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === "databricks") {
      // Fetch clusters if workspace is already selected
      const firstDbAccount = accountOptions.find(
        (opt) => opt.type === "databricks",
      );
      if (firstDbAccount) {
        setSelectedDatabricksAccount(firstDbAccount);
        setSelectedAccount(firstDbAccount.value);
        setTimeout(() => {
          fetchDbClusters(firstDbAccount.accountId);
        }, 100);
      } else {
        message.info("No Databricks accounts available");
      }
    } else {
      // For AWS tab, ensure an AWS account is selected
      const awsAccount = accountOptions.find((opt) => opt.type === "aws");
      if (awsAccount) {
        setSelectedAccount(awsAccount.value);
      }
    }
  };

  const paginatedServices = filteredServices.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const filteredDbClusters = dbClusters.filter(
    (cluster) =>
      cluster.cluster_name?.toLowerCase().includes(dbSearch.toLowerCase()) ||
      cluster.workspace_name?.toLowerCase().includes(dbSearch.toLowerCase()) ||
      cluster.account_id?.toLowerCase().includes(dbSearch.toLowerCase()),
  );

  const paginatedDbClusters = filteredDbClusters.slice(
    (dbPage - 1) * dbPageSize,
    dbPage * dbPageSize,
  );
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Tabs
        className="page-tabs-fill"
        activeKey={activeTab}
        onChange={handleTabChange}
        destroyInactiveTabPane={true}
        style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
        items={[
          {
            key: "aws",
            label: (
              <span style={{ color: activeTab === "aws" ? "#1890ff" : "#666" }}>
                AWS Metrics
              </span>
            ),
            children: (
              <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div
                  style={{
                    flexShrink: 0,
                    marginBottom: 16,
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <h2 style={{ color: isDark ? "#e6edf3" : undefined }}>AWS Metrics Summary</h2>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <Input
                      allowClear
                      placeholder="Search Metrics"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      className="metrics-page-aws-search-input"
                      style={{
                        width: 300,
                        borderRadius: 8,
                        border: isDark
                          ? "1px solid rgba(255, 255, 255, 0.22)"
                          : "1px solid #d9d9d9",
                        background: isDark ? "rgba(0, 0, 0, 0.25)" : "#fff",
                      }}
                    />
                    <Tooltip title="Refresh">
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                          const awsAccountId = selectedAccount.replace(
                            "aws_",
                            "",
                          );
                          fetchServices(awsAccountId);
                        }}
                        loading={loading}
                        disabled={!selectedAccount}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      />
                    </Tooltip>
                    <Select
                      placeholder="Select Account"
                      options={accountOptions.filter(
                        (opt) => opt.type === "aws",
                      )}
                      style={{ width: "100%", maxWidth: "250px" }}
                      onChange={handleAccountChange}
                      value={selectedAccount}
                    />
                    <Button
                      className="create-btn"
                      type="primary"
                      icon={<PlusOutlined />}
                      size="large"
                      onClick={() => {
                        setModalVisible(true);
                        setEditingService(null);
                        setTags([]);
                        form.resetFields();
                        form.setFieldsValue({
                          account: selectedAccount,
                          region: selectedRegion,
                        });
                      }}
                      disabled={!selectedAccount || isViewer}
                    >
                      Add Metrics
                    </Button>
                  </div>
                </div>
                {/* Stats Cards - always visible; show 0 when no account selected */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24, flexShrink: 0 }}>
                  <Col xs={24} sm={12} md={8}>
                    <Card 
                      bordered={false}
                      style={{ 
                        borderRadius: 12,
                        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    >
                      <Statistic
                        title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Total Services</span>}
                        value={selectedAccount ? metricsStats.total : 0}
                        valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                        prefix={<DatabaseOutlined style={{ color: '#fff', fontSize: 24 }} />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card 
                      bordered={false}
                      style={{ 
                        borderRadius: 12,
                        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      }}
                    >
                      <Statistic
                        title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Active Services</span>}
                        value={selectedAccount ? metricsStats.active : 0}
                        valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                        prefix={<CheckCircleOutlined style={{ color: '#fff', fontSize: 24 }} />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card 
                      bordered={false}
                      style={{ 
                        borderRadius: 12,
                        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      }}
                    >
                      <Statistic
                        title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Inactive Services</span>}
                        value={selectedAccount ? metricsStats.inactive : 0}
                        valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                        prefix={<CloseCircleOutlined style={{ color: '#fff', fontSize: 24 }} />}
                      />
                    </Card>
                  </Col>
                </Row>
                {/* <Table      
        className="custom-ant-table rounded-rows-table-metrics"
        columns={columns}
        dataSource={services.filter(service => service.name.toLowerCase().includes(searchText.toLowerCase()))}
        loading={loading}
        rowKey="name"
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: [10, 20, 50, 100],
          position: ['bottomCenter'],
          showQuickJumper: false,
          selectComponentClass: null
        }}       
        scroll={{ x: 'max-content'}}      
        
      /> */}

                {/* Stats Cards */}
                {/* <Row gutter={16} style={{ marginBottom: 24 }}>
  <Col span={8}>
    <Card style={{ borderRadius: 12, textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff' }}>
        {metricsStats.total}
      </div>
      <div style={{ fontSize: 14, color: '#8c8c8c', marginTop: 4 }}>
        Total Services
      </div>
    </Card>
  </Col>
  <Col span={8}>
    <Card style={{ borderRadius: 12, textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
        {metricsStats.active}
      </div>
      <div style={{ fontSize: 14, color: '#8c8c8c', marginTop: 4 }}>
        Active
      </div>
    </Card>
  </Col>
  <Col span={8}>
    <Card style={{ borderRadius: 12, textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#ff4d4f' }}>
        {metricsStats.inactive}
      </div>
      <div style={{ fontSize: 14, color: '#8c8c8c', marginTop: 4 }}>
        Inactive
      </div>
    </Card>
  </Col>
</Row> */}

                {/* Column Headers - static */}
                <div
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "12px 20px",
                    background: isDark ? "#1c2128" : "#fafafa",
                    border: isDark ? "1px solid #30363d" : "none",
                    borderRadius: 8,
                    marginBottom: 12,
                    fontWeight: 600,
                    fontSize: 13,
                    color: isDark ? "#adbac7" : "#666",
                  }}
                >
                  <div style={{ width: 40, color: isDark ? "#adbac7" : undefined }}>SI</div>
                  <div style={{ flex: 1, color: isDark ? "#adbac7" : undefined }}>SERVICE NAME</div>
                  <div style={{ width: 150, color: isDark ? "#adbac7" : undefined }}>REGIONS</div>
                  <div style={{ width: 100, color: isDark ? "#adbac7" : undefined }}>STATUS</div>
                  <div style={{ width: 200, color: isDark ? "#adbac7" : undefined }}>METRICS</div>
                  <div style={{ width: 150, color: isDark ? "#adbac7" : undefined }}>ACTIONS</div>
                </div>

                {/* Service Cards - scrollable */}
                <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {paginatedServices.map((service, index) => (
                  <Card
                    key={service.key}
                    style={{
                      marginBottom: 12,
                      borderRadius: 12,
                      borderLeft: `4px solid ${service.status === "ACTIVE" ? "#52c41a" : "#ff4d4f"}`,
                      background: isDark ? "#161b22" : "#fff",
                      border: isDark ? "1px solid #30363d" : "1px solid #e8e8e8",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 16 }}
                    >
                      {/* SI Number */}
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 8,
                          background: isDark ? "#21262d" : "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          color: isDark ? "#adbac7" : "#666",
                        }}
                      >
                        {String((page - 1) * pageSize + index + 1).padStart(
                          2,
                          "0",
                        )}
                      </div>

                      {/* Service Info */}
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 15,
                            marginBottom: 4,
                            color: isDark ? "#e6edf3" : "#262626",
                          }}
                        >
                          {service.name}
                        </div>
                      </div>

                      {/* Regions */}
                      <div style={{ width: 150 }}>
                        <Space wrap>
                          {service.regions?.map((region) => (
                            <Tag
                              key={region}
                              color="blue"
                              style={{ margin: 0 }}
                            >
                              {region}
                            </Tag>
                          ))}
                        </Space>
                      </div>

                      {/* Status */}
                      <div style={{ width: 100 }}>
                        <Tag
                          style={{
                            background:
                              service.status === "ACTIVE"
                                ? "#16a34a"
                                : "#ff4d4f",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            fontWeight: 500,
                            padding: "4px 16px",
                          }}
                        >
                          {service.status === "ACTIVE" ? "Active" : "Inactive"}
                        </Tag>
                      </div>

                      {/* Metrics Count */}
                      <div style={{ width: 200 }}>
                        <Tag color="blue">
                          {service.metrics?.length || 0} metrics
                        </Tag>
                      </div>

                      {/* Actions */}
                      <Space size={8} style={{ width: 150 }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View">
                          <Button
                            icon={<EyeOutlined />}
                            type="text"
                            size="small"
                            onClick={() => {
                              setViewingService(service);
                              setViewModalVisible(true);
                            }}
                          />
                        </Tooltip>
                        <Tooltip title="Edit">
                          <Button
                            icon={<EditFilled />}
                            type="text"
                            size="small"
                            onClick={() => handleEditService(service)}
                            disabled={isViewer}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="Delete Metrics?"
                          onConfirm={() => handleDeleteService(service.serviceType || service.name)}
                        >
                          <Tooltip title="Delete">
                            <Button
                              icon={<DeleteFilled />}
                              type="text"
                              danger
                              size="small"
                              disabled={isViewer}
                            />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    </div>
                  </Card>
                ))}
                </div>

                {/* Pagination - static footer */}
                <div
                  style={{
                    flexShrink: 0,
                    marginTop: 16,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={filteredServices.length}
                    onChange={(p) => setPage(p)}
                    showSizeChanger={false}
                  />
                </div>
              </div>
            ),
          },
          // {
          //   key: 'databricks',
          //   label: (
          //     <span style={{ color: activeTab === 'databricks' ? '#1890ff' : '#666' }}>
          //       Databricks Metrics
          //     </span>
          //   ),
          //   children: (
          //     <div style={{ textAlign: 'center', padding: '40px' }}>
          //       <Empty
          //         description={
          //           databricksAccountCount > 0
          //             ? "Select a Databricks account from the accounts page to view metrics"
          //             : "No Databricks accounts available. Please onboard a Databricks account first."
          //         }
          //       />
          //     </div>
          //   ),
          // },
          {
            key: "databricks",
            label: (
              <span
                style={{
                  color: activeTab === "databricks" ? "#1890ff" : "#666",
                }}
              >
                Databricks Metrics
              </span>
            ),
            children: (
              <div>
                {/* Header Section */}
                <div
                  style={{
                    marginBottom: 16,
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <h2 style={{ color: isDark ? "#e6edf3" : undefined }}>Databricks Metrics Summary</h2>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <Input
                      bordered={false}
                      placeholder="Search Clusters"
                      value={dbSearch}
                      onChange={(e) => setDbSearch(e.target.value)}
                      style={{ width: 300 }}
                      allowClear
                    />
                    <Tooltip title="Refresh">
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                          const accountId = selectedDatabricksAccount?.accountId || selectedAccount?.replace("databricks_", "");
                          if (accountId) {
                            fetchDbClusters(accountId);
                          } else {
                            message.info("Select a Databricks account first");
                          }
                        }}
                        loading={loading}
                        disabled={!selectedDatabricksAccount && !selectedAccount}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      />
                    </Tooltip>
                    <Select
                      placeholder="Select Account"
                      style={{ width: "100%", maxWidth: "250px" }}
                      // value={selectedDatabricksAccount?.value || `databricks_${selectedDatabricksAccount?.accountId}`}
                      value={selectedDatabricksAccount?.value}
                      onChange={handleAccountChange}
                      options={accountOptions.filter(
                        (opt) => opt.type === "databricks",
                      )}
                    />
                    {/* <Select 
                      placeholder="Select Workspace" 
                      style={{ width: "100%", maxWidth: "250px" }} 
                      value={selectedWorkspace}
                      onChange={handleWorkspaceChange}
                      options={workspaceOptions}
                    /> */}
                    <Button
                      className="create-btn"
                      type="primary"
                      icon={<PlusOutlined />}
                      size="large"
                      onClick={() => {
                        fetchDatabricksWorkspaces();
                        setEnableMetricsModal(true);
                      }}
                      disabled={isViewer}
                    >
                      Enable Metrics
                    </Button>
                  </div>
                </div>
                {/* Databricks Stats Cards - always visible; show 0 when no account selected */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                  <Col xs={24} sm={12} md={8}>
                    <Card 
                      bordered={false}
                      style={{ 
                        borderRadius: 12,
                        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    >
                      <Statistic
                        title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Total Clusters</span>}
                        value={selectedDatabricksAccount ? dbStats.total : 0}
                        valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                        prefix={<DatabaseOutlined style={{ color: '#fff', fontSize: 24 }} />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card 
                      bordered={false}
                      style={{ 
                        borderRadius: 12,
                        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      }}
                    >
                      <Statistic
                        title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Active Clusters</span>}
                        value={selectedDatabricksAccount ? dbStats.active : 0}
                        valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                        prefix={<CheckCircleOutlined style={{ color: '#fff', fontSize: 24 }} />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card 
                      bordered={false}
                      style={{ 
                        borderRadius: 12,
                        boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      }}
                    >
                      <Statistic
                        title={<span style={{ color: 'rgba(255,255,255,0.9)' }}>Inactive Clusters</span>}
                        value={selectedDatabricksAccount ? dbStats.inactive : 0}
                        valueStyle={{ color: '#fff', fontSize: 32, fontWeight: 700 }}
                        prefix={<CloseCircleOutlined style={{ color: '#fff', fontSize: 24 }} />}
                      />
                    </Card>
                  </Col>
                </Row>
                {/* Column Headers */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "12px 20px",
                    background: isDark ? "#1c2128" : "#fafafa",
                    border: isDark ? "1px solid #30363d" : "none",
                    borderRadius: 8,
                    marginBottom: 12,
                    fontWeight: 600,
                    fontSize: 13,
                    color: isDark ? "#adbac7" : "#666",
                  }}
                >
                  <div style={{ width: 40, color: isDark ? "#adbac7" : undefined }}>SI</div>
                  <div style={{ flex: 1, color: isDark ? "#adbac7" : undefined }}>CLUSTER NAME</div>
                  <div style={{ width: 150, color: isDark ? "#adbac7" : undefined }}>WORKSPACE</div>
                  <div style={{ width: 100, color: isDark ? "#adbac7" : undefined }}>STATUS</div>
                  <div style={{ width: 150, color: isDark ? "#adbac7" : undefined }}>ACCOUNT ID</div>
                  <div style={{ width: 150, color: isDark ? "#adbac7" : undefined }}>ACTIONS</div>
                </div>

                {/* Cluster Cards */}
                {paginatedDbClusters.map((cluster, index) => (
                  <Card
                    key={cluster.cluster_id}
                    style={{
                      marginBottom: 12,
                      borderRadius: 12,
                      borderLeft: `4px solid ${
                        ["enabled", "active"].includes(
                          String(cluster.status || "")
                            .toLowerCase()
                            .trim(),
                        )
                          ? "#52c41a"
                          : "#ff4d4f"
                      }`,
                      background: isDark ? "#161b22" : "#fff",
                      border: isDark ? "1px solid #30363d" : "1px solid #e8e8e8",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 16 }}
                    >
                      {/* SI Number */}
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 8,
                          background: isDark ? "#21262d" : "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 600,
                          color: isDark ? "#adbac7" : "#666",
                        }}
                      >
                        {String((dbPage - 1) * dbPageSize + index + 1).padStart(
                          2,
                          "0",
                        )}
                      </div>

                      {/* Cluster Info */}
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 15,
                            marginBottom: 4,
                            color: isDark ? "#e6edf3" : "#262626",
                          }}
                        >
                          {cluster.cluster_name}
                        </div>
                      </div>

                      {/* Workspace */}
                      <div style={{ width: 150 }}>
                        <Tag color="blue" style={{ margin: 0 }}>
                          {cluster.workspace_name}
                        </Tag>
                      </div>

                      {/* Status */}
                      <div style={{ width: 100 }}>
                        <Tag
                          style={{
                            background: ["enabled", "active"].includes(
                              String(cluster.status || "")
                                .toLowerCase()
                                .trim(),
                            )
                              ? "#16a34a"
                              : "#ff4d4f",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            fontWeight: 500,
                            padding: "4px 16px",
                          }}
                        >
                          {["enabled", "active"].includes(
                            String(cluster.status || "")
                              .toLowerCase()
                              .trim(),
                          )
                            ? "Active"
                            : "Inactive"}
                        </Tag>
                      </div>

                      {/* Account ID */}
                      <div style={{ width: 150 }}>
                        <span style={{ fontSize: 13, color: isDark ? "#adbac7" : "#666" }}>
                          {cluster.account_id}
                        </span>
                      </div>

                      {/* Actions */}
                      <Space size={8} style={{ width: 150 }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View">
                          <Button
                            icon={<EyeOutlined />}
                            type="text"
                            size="small"
                            onClick={() => handleViewCluster(cluster)}
                          />
                        </Tooltip>
                        <Tooltip title="Edit">
                          <Button
                            icon={<EditFilled />}
                            type="text"
                            size="small"
                            onClick={() => handleEditCluster(cluster)}
                            disabled={isViewer}
                          />
                        </Tooltip>
                        <Popconfirm
                          title="Delete Cluster Metrics?"
                          onConfirm={() =>
                            handleDeleteCluster(cluster.cluster_id)
                          }
                        >
                          <Tooltip title="Delete">
                            <Button
                              icon={<DeleteFilled />}
                              type="text"
                              danger
                              size="small"
                              disabled={isViewer}
                            />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    </div>
                  </Card>
                ))}

                {/* Pagination */}
                <div
                  style={{
                    marginTop: 16,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    current={dbPage}
                    pageSize={dbPageSize}
                    total={filteredDbClusters.length}
                    onChange={(p) => setDbPage(p)}
                    showSizeChanger={false}
                  />
                </div>
              </div>
            ),
          },
        ]}
      />

      <Modal
        title={editingService ? "Edit Metrics" : "Add Metrics"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingService(null);
          form.resetFields();
          setTags([]);
          setCategorizedMetrics({});
          setMetricOptions([]);

          setCostData(null);
          setCostError(null);
        }}
        onOk={() => form.submit()}
        width={800}
        okText="Submit"
        confirmLoading={loading}
        okButtonProps={{
          disabled: !editingService && !costData,
          title:
            !editingService && !costData
              ? "Fill all required fields to enable submit"
              : "",
        }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingService ? handleUpdateService : handleCreateService}
          requiredMark={false}
          onValuesChange={handleFormValuesChange}
        >
          {/* Account Field */}
          <Form.Item
            name="account"
            label={
              <span>
                Account <span style={{ color: "red" }}>*</span>
              </span>
            }
            rules={[{ required: true, message: "Please select an account" }]}
          >
            <Select
              placeholder="Select Account"
              options={accountOptions}
              disabled={true}
            />
          </Form.Item>

          {/* Region Field */}

          <Form.Item
            name="region"
            label={
              <span>
                Region <span style={{ color: "red" }}>*</span>
              </span>
            }
            rules={[{ required: true, message: "Please select a region" }]}
          >
            <Select
              placeholder="Select region"
              options={regionOptions}
              disabled={!!editingService}
              onChange={(value) => {
                if (selectedAccount) {
                  form.setFieldValue("type", undefined);
                  form.setFieldValue("metrics", []);
                  setSelectedRegion(value);
                  fetchNamespaces(selectedAccount, value);
                }
              }}
            />
          </Form.Item>

          <Form.Item
            name="type"
            label={
              <span>
                Service Type <span style={{ color: "red" }}>*</span>
              </span>
            }
            rules={[
              { required: true, message: "Select or type a CloudWatch namespace" },
            ]}
          >
            {editingService ? (
              <Select
                placeholder="Service type"
                disabled
                showSearch
                options={[
                  {
                    value: editingService.serviceType || editingService.type,
                    label:
                      editingService.displayName
                      || displayServiceName(
                        editingService.serviceType || editingService.type,
                        editingService.namespace,
                      ),
                  },
                ]}
              />
            ) : (
              <AutoComplete
                onSelect={(v) => handleNamespaceChange(v)}
                onBlur={() => {
                  const raw = form.getFieldValue("type");
                  const v = typeof raw === "string" ? raw.trim() : "";
                  if (v && selectedAccount && selectedRegion) {
                    handleNamespaceChange(v);
                  }
                }}
                placeholder={
                  namespaceLoading
                    ? "Loading namespaces..."
                    : "Search or type any CloudWatch namespace (e.g. AWS/Lambda)"
                }
                disabled={namespaceLoading}
                allowClear
                options={namespaceOptions}
                filterOption={(inputValue, option) =>
                  String(option?.label ?? option?.value ?? "")
                    .toLowerCase()
                    .includes(String(inputValue).toLowerCase())
                }
                notFoundContent={
                  namespaceLoading ? "Loading..." : "Type a namespace to continue"
                }
                style={{ width: "100%" }}
                listHeight={360}
              />
            )}
          </Form.Item>

          {/* Metrics Field */}
          <Form.Item
            name="metrics"
            label={
              <span>
                Metrics <span style={{ color: "red" }}>*</span>
              </span>
            }
            rules={[
              {
                validator: async (_, metrics) => {
                  if (!metrics || metrics.length === 0) {
                    return Promise.reject(
                      new Error("Please select at least one metric."),
                    );
                  }
                  for (const metric of metrics) {
                    if (!metric.statistics || metric.statistics.length === 0) {
                      return Promise.reject(
                        new Error(
                          `Metric "${metric.name}" must have at least one statistic.`,
                        ),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <MetricsSelector
              metricOptions={metricOptions}
              metricsLoading={metricsLoading}
              onEnsureMetricsLoaded={ensureMetricsLoaded}
              onMetricsSaved={handleMetricsSaved}
              serviceType={
                editingService?.serviceType
                || editingService?.type
                || selectedNamespace
                || ""
              }
            />
          </Form.Item>

          {/* Period Field */}
          <Form.Item
            name="period"
            label={
              <span>
                Period <span style={{ color: "red" }}>*</span>
              </span>
            }
            rules={[{ required: true, message: "Please enter period" }]}
            initialValue={DEFAULT_SCRAPE_PERIOD}
          >
            <InputNumber
              min={1}
              step={1}
              placeholder="Enter period in seconds"
              style={{ width: "100%" }}
            />
          </Form.Item>

          {/* Length Field */}
          <Form.Item
            name="length"
            label={
              <span>
                Length <span style={{ color: "red" }}>*</span>
              </span>
            }
            rules={[{ required: true, message: "Please enter length" }]}
            initialValue={DEFAULT_SCRAPE_LENGTH}
          >
            <InputNumber
              min={1}
              step={1}
              placeholder="Enter length in seconds"
              style={{ width: "100%" }}
            />
          </Form.Item>

          {/* Tags UI */}

          <Form.Item
            label={
              <span>
                Tags
                <Tooltip title="Tags help organize your AWS resources and can be used for filtering and cost allocation">
                </Tooltip>
              </span>
            }
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Row gutter={8}>
                <Col flex="1">
                  <Input
                    placeholder="Key"
                    value={tagKey}
                    onChange={(e) => setTagKey(e.target.value)}
                    onKeyPress={handleTagKeyPress}
                  />
                </Col>
                <Col flex="1">
                  <Input
                    placeholder="Value"
                    value={tagValue}
                    onChange={(e) => setTagValue(e.target.value)}
                    onKeyPress={handleTagKeyPress}
                  />
                </Col>
                <Col flex="0 1 80px">
                  <Button
                    type="primary"
                    onClick={handleAddTag}
                    disabled={!tagKey || !tagValue}
                    style={{ width: "100%" }}
                  >
                    Add
                  </Button>
                </Col>
              </Row>

              {tags.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {tags.map((tag, index) => (
                    <Tag
                      key={index}
                      closable
                      onClose={() => handleRemoveTag(index)}
                      style={{ marginBottom: 4 }}
                    >
                      {tag.key}: {tag.tag_name}
                    </Tag>
                  ))}
                </div>
              )}

              <div style={{ color: "#8c8c8c", fontSize: "12px", marginTop: 8 }}>
                Adding tags is optional but recommended for better organization
              </div>
            </Space>
          </Form.Item>

          {/* Cost Calculation Result */}
          {!editingService && (
            <Form.Item
              label={
                <span style={{ fontWeight: 500 }}>
                  Estimated Cost
                  <Tooltip title="Estimated cost for collecting these CloudWatch metrics">
                  </Tooltip>
                </span>
              }
            >
              {!formComplete && !costData && !costLoading && (
                <Alert
                  message="Complete all required fields to see cost estimate"
                  type="info"
                  style={{ marginBottom: 8 }}
                />
              )}

              {costLoading && (
                <div
                  style={{
                    padding: 16,
                    background: "#f5f5f5",
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                  }}
                >
                  <Spin size="small" style={{ marginBottom: 12 }} />
                  <div style={{ textAlign: "center" }}>
                    <div>Calculating cost estimate...</div>
                    <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                      This may take a moment
                    </div>
                  </div>
                </div>
              )}

              {costError && (
                <Alert
                  message="Error calculating cost"
                  description={costError}
                  type="error"
                  style={{ marginBottom: 8 }}
                />
              )}

              {costData && (
                <Card
                  size="small"
                  bordered={false}
                  style={{
                    background: isDark
                      ? "rgba(82, 196, 26, 0.18)"
                      : "rgba(82, 196, 26, 0.12)",
                    borderRadius: 8,
                  }}
                >
                  <Row gutter={16} align="middle">
                    <Col>
                    </Col>
                    <Col>
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 16,
                            color: isDark ? "#f6ffed" : "#1f1f1f",
                          }}
                        >
                          Estimated Cost: ${costData.daily_costing?.toFixed(5)}{" "}
                          USD / day
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            color: isDark ? "rgba(255,255,255,0.85)" : "#434343",
                            marginTop: 4,
                          }}
                        >
                          Monthly: approximately $
                          {costData.monthly_costing?.toFixed(5)} USD
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: isDark ? "rgba(255,255,255,0.65)" : "#595959",
                            marginTop: 6,
                          }}
                        >
                          <span style={{ color: "#ff4d4f" }}>*</span> Request-based
                          estimate (CloudWatch API request math), shown per
                          individual monitored resource count.
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              )}
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* View Metrics Modal */}
      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ marginRight: "8px", fontSize: "18px" }}>
                {viewingService?.name || "Metrics Details"}
              </span>
              {viewingService && (
                <Tag
                  color={
                    viewingService.status === "ACTIVE" ? "success" : "error"
                  }
                >
                  {viewingService.status}
                </Tag>
              )}
            </div>
            {viewingService && viewingService.metrics?.length > 0 && (
              <Input.Search
                placeholder="Filter metrics"
                style={{ width: 200, marginRight: "20px" }}
                size="small"
                allowClear
                id="metrics-search-input"
                onInput={(e) => {
                  const searchText = e.target.value.toLowerCase();
                  const metricTags = document.querySelectorAll(".metric-tag");
                  metricTags.forEach((tag) => {
                    const metricName = tag
                      .getAttribute("data-metric-name")
                      .toLowerCase();
                    if (metricName.includes(searchText)) {
                      tag.style.display = "inline-flex";
                    } else {
                      tag.style.display = "none";
                    }
                  });
                }}
              />
            )}
          </div>
        }
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setViewingService(null);
        }}
        footer={[
          <Button
            key="edit"
            onClick={() => {
              setViewModalVisible(false);
              handleEditService(viewingService);
            }}
            disabled={isViewer}
          >
            Edit Metrics
          </Button>,
          <Button
            key="close"
            type="primary"
            onClick={() => setViewModalVisible(false)}
          >
            Close
          </Button>,
        ]}
        width={800}
        destroyOnClose
        bodyStyle={{ padding: "16px", maxHeight: "70vh", overflowY: "auto" }}
      >
        {viewingService && (
          <div>
            <Card
              size="small"
              bordered={false}
              className="metrics-view-summary-card"
              style={{
                width: "720px",
                minHeight: "69px",
                borderRadius: "8px",
                paddingTop: "12px",
                paddingBottom: "12px",
                marginBottom: "12px",
              }}
            >
              <Row gutter={[24, 8]}>
                <Col xs={12} sm={8} md={6}>
                  <div
                    style={{
                      fontSize: "13px",
                      color: isDark ? "#adbac7" : "#666",
                      marginBottom: "2px",
                    }}
                  >
                    Service Name
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: isDark ? "#e6edf3" : "#333",
                    }}
                  >
                    {viewingService.name}
                  </div>
                </Col>

                <Col xs={12} sm={8} md={6}>
                  <div
                    style={{
                      fontSize: "13px",
                      color: isDark ? "#adbac7" : "#666",
                      marginBottom: "2px",
                    }}
                  >
                    Period
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: isDark ? "#e6edf3" : "#333",
                    }}
                  >
                    {viewingService.period} sec
                  </div>
                </Col>

                <Col xs={12} sm={8} md={6}>
                  <div
                    style={{
                      fontSize: "13px",
                      color: isDark ? "#adbac7" : "#666",
                      marginBottom: "2px",
                    }}
                  >
                    Length
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: isDark ? "#e6edf3" : "#333",
                    }}
                  >
                    {viewingService.length} sec
                  </div>
                </Col>

                <Col xs={12} sm={8} md={6}>
                  <div
                    style={{
                      fontSize: "13px",
                      color: isDark ? "#adbac7" : "#666",
                      marginBottom: "2px",
                    }}
                  >
                    Regions
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: isDark ? "#e6edf3" : "#333",
                    }}
                  >
                    {viewingService.regions?.map((region) => (
                      <Tag
                        color="green"
                        key={region}
                        style={{ margin: "0 4px 4px 0", fontSize: "12px" }}
                      >
                        {region}
                      </Tag>
                    ))}
                  </div>
                </Col>
              </Row>

              {viewingService.tags?.length > 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    paddingTop: "12px",
                    borderTop: `1px solid ${isDark ? "#30363d" : "#f0f0f0"}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      color: isDark ? "#adbac7" : "#666",
                      marginBottom: "4px",
                    }}
                  >
                    Tags
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
                  >
                    {viewingService.tags.map((tag, index) => (
                      <Tag
                        color="purple"
                        key={`${tag.key}-${tag.tag_name}-${index}`}
                        style={{ margin: "0 0 4px 0" }}
                      >
                        <span style={{ fontWeight: "500" }}>{tag.key}:</span>{" "}
                        {tag.tag_name}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <div
              style={{
                marginTop: "16px",
                marginBottom: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "16px", fontWeight: "500", color: isDark ? "#e6edf3" : "inherit" }}>
                Metrics ({viewingService.metrics?.length || 0})
              </span>
            </div>

            {viewingService.metrics?.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  marginBottom: "8px",
                }}
              >
                {viewingService.metrics.map((metric) => (
                  <div
                    key={metric.name}
                    className="metric-tag metrics-view-row"
                    data-metric-name={metric.name}
                    style={{
                      width: "100%",
                      minWidth: "697px",
                      minHeight: "30px",
                      position: "relative",
                      paddingTop: "8px",
                      paddingRight: "12px",
                      paddingBottom: "8px",
                      paddingLeft: "12px",
                      borderRadius: "6px",
                    }}
                  >
                    {/* Metric Name */}
                    <div
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "8px",
                        width: "300px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: isDark ? "#e6edf3" : "#333",
                        wordWrap: "break-word",
                        lineHeight: "1.4",
                        overflow: "hidden",
                      }}
                    >
                      {metric.name}
                    </div>

                    {/* Statistics Container - Fixed Position */}
                    {metric.statistics?.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          left: "330px",
                          top: "8px",
                          right: "12px",
                          minHeight: "20px",
                          paddingLeft: "12px",
                          display: "flex",
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {metric.statistics?.map((stat, index) => (
                          <Tag
                            key={`${metric.name}-${stat}-${index}`}
                            color="blue"
                            style={{
                              fontSize: "12px",
                              padding: "2px 6px",
                              margin: "0",
                              lineHeight: "1.4",
                              minHeight: "20px",
                              display: "inline-flex",
                              alignItems: "center",
                              borderRadius: "4px",
                            }}
                          >
                            {stat}
                          </Tag>
                        ))}
                      </div>
                    )}

                    {/* No Statistics Message - Fixed Position */}
                    {(!metric.statistics || metric.statistics.length === 0) && (
                      <div
                        style={{
                          position: "absolute",
                          left: "330px",
                          top: "8px",
                          right: "12px",
                          minHeight: "20px",
                          paddingLeft: "12px",
                          display: "flex",
                          alignItems: "center",
                          fontSize: "12px",
                          color: isDark ? "#8b949e" : "#999",
                          fontStyle: "italic",
                        }}
                      >
                        No statistics defined
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Empty
                description={
                  <span style={{ color: "#999" }}>
                    No metrics defined for this service
                  </span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </div>
        )}
      </Modal>

      {/* Databricks Workspace Selection Modal */}
      {/* <Modal
        title="Select Workspace"
        open={databricksWorkspaceModalVisible}
        onCancel={handleWorkspaceModalCancel}
        footer={[
          <Button key="cancel" onClick={handleWorkspaceModalCancel}>
            Cancel
          </Button>
        ]}
        width={700}
        style={{ top: 100 }}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '24px' }}>
            <p style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
              Account ID:
            </p>
            <p style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '500', color: '#333', wordBreak: 'break-all' }}>
              {selectedDatabricksAccount?.accountId || 'N/A'}
            </p>
          </div>
          <div>
            <p style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
              Select Workspace ID:
            </p>
            <Select
              placeholder="Select a workspace"
              style={{ width: '100%', height: '48px' }}
              size="large"
              loading={workspaceLoading}
              onChange={handleWorkspaceSelect}
              notFoundContent={workspaceLoading ? <Spin size="small" /> : 'No workspaces available'}
              options={databricksWorkspaces.map((ws) => ({
                value: ws.workspace_id,
                label: ws.workspace_name || `Workspace ${ws.workspace_id}`,
              }))}
            />
          </div>
          {databricksWorkspaces.length === 0 && !workspaceLoading && (
            <Alert
              message="No workspaces found"
              description="Unable to fetch workspaces. Please ensure the account credentials are correct."
              type="warning"style={{ marginTop: '16px' }}
            />
          )}
        </div>
      </Modal> */}
      {/* Enable Databricks Metrics Modal */}
      <Modal
        title="Enable Databricks Metrics"
        open={enableMetricsModal}
        afterOpenChange={(open) => {
          if (open && selectedDatabricksAccount && databricksWorkspaces.length === 0) {
            // Fetch workspaces when modal opens if not already loaded
            console.log('[Enable Metrics Modal] Fetching workspaces on open...');
            fetchDatabricksWorkspaces();
          }
        }}
        onCancel={() => {
          setEnableMetricsModal(false);
          enableMetricsForm.resetFields();
          setRawClusters([]);
          setInstanceProfiles([]);
        }}
        onOk={async () => {
          try {
            const values = await enableMetricsForm.validateFields();
            setLoading(true);

            const accountId = selectedDatabricksAccount?.accountId;
            const workspace = databricksWorkspaces.find(
              (w) => w.workspace_id === values.workspace_id,
            );

            // Extract deployment_name from workspace_url if missing
            let deploymentName = workspace?.deployment_name;
            if (!deploymentName && workspace?.workspace_url) {
              const urlParts = workspace.workspace_url.split('//');
              if (urlParts.length > 1) {
                deploymentName = urlParts[1].split('.')[0];
                console.log('[MetricsPage Enable Metrics] Extracted deployment_name from URL:', deploymentName);
              }
            }
            
            if (!deploymentName || !deploymentName.trim()) {
              message.error('Workspace deployment name is missing. Please select the workspace again.');
              setLoading(false);
              return;
            }

            // Validate instance profile ARN
            if (!values.instance_profile_arn || !values.instance_profile_arn.trim()) {
              message.error("Instance Profile ARN is required. Please select an instance profile.");
              setLoading(false);
              return;
            }

            // Enable metrics for each selected cluster
            const accountName = selectedDatabricksAccount?.accountName;
            for (const clusterId of values.cluster_ids) {
              await databricksService.toggleMetrics(
                values.workspace_id,
                deploymentName,
                clusterId,
                true,
                accountId,  // Pass account_id as fallback
                accountName,  // Prefer account_name for OAuth token generation
                null,  // No PAT token - backend will use OAuth from account_name/account_id
                values.instance_profile_arn,
              );
            }

            message.success("Metrics enabled successfully");
            setEnableMetricsModal(false);
            enableMetricsForm.resetFields();
            setRawClusters([]);
            setInstanceProfiles([]);
            fetchDbClusters();
          } catch (error) {
            message.error(
              "Failed to enable metrics: " + (error.message || "Unknown error"),
            );
          } finally {
            setLoading(false);
          }
        }}
        confirmLoading={loading}
        width={600}
      >
        <Form form={enableMetricsForm} layout="vertical">
          <Form.Item label="Account ID">
            <Input disabled value={selectedDatabricksAccount?.accountId} />
          </Form.Item>

          <Form.Item
            label="Workspace"
            name="workspace_id"
            rules={[{ required: true, message: "Please select workspace" }]}
          >
            <Select
              placeholder="Select workspace"
              loading={workspaceLoading}
              notFoundContent={workspaceLoading ? <Spin size="small" /> : 'No workspaces available. Click to refresh.'}
              onFocus={() => {
                // Fetch workspaces when dropdown is focused if not already loaded
                if (databricksWorkspaces.length === 0 && !workspaceLoading && selectedDatabricksAccount) {
                  fetchDatabricksWorkspaces();
                }
              }}
              onChange={(workspaceId) => {
                const workspace = databricksWorkspaces.find(
                  (w) => w.workspace_id === workspaceId,
                );
                if (!workspace) return;
                
                // Extract deployment_name from workspace_url if missing
                let deploymentName = workspace.deployment_name;
                if (!deploymentName && workspace.workspace_url) {
                  const urlParts = workspace.workspace_url.split('//');
                  if (urlParts.length > 1) {
                    deploymentName = urlParts[1].split('.')[0];
                    console.log('[MetricsPage Workspace onChange] Extracted deployment_name from URL:', deploymentName);
                  }
                }
                
                if (!deploymentName || !deploymentName.trim()) {
                  console.error('[MetricsPage Workspace onChange] ERROR: deployment_name is missing or empty after extraction');
                  message.error('Workspace deployment name is missing. Please select the workspace again.');
                  return;
                }
                
                const accountId = selectedDatabricksAccount?.accountId;
                
                // Validate: account_id must be provided (OAuth is required)
                if (!accountId) {
                  message.warning("Account must be selected to use OAuth authentication");
                  return;
                }
                
                fetchRawClusters(workspaceId, deploymentName);
              }}
              options={databricksWorkspaces.map((w) => ({
                value: w.workspace_id,
                label: w.workspace_name || w.workspace_id,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Clusters"
            name="cluster_ids"
            rules={[
              { required: true, message: "Please select at least one cluster" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select clusters to enable metrics"
              loading={loading}
              options={rawClusters.map((c) => ({
                value: c.cluster_id,
                label: c.cluster_name || c.name || c.cluster_id,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Instance Profile ARN"
            name="instance_profile_arn"
            rules={[
              { required: true, message: "Please select an instance profile" },
            ]}
          >
            <Select
              placeholder="Select instance profile"
              loading={loadingInstanceProfiles}
              options={instanceProfiles.map((profile) => ({
                value: profile.arn,
                label: profile.name || profile.arn,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
      {/* View Databricks Cluster Modal */}
      {/* View Databricks Cluster Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>Cluster Details</span>
          </div>
        }
        open={viewDbModalVisible}
        onCancel={() => setViewDbModalVisible(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setViewDbModalVisible(false)}
          >
            Close
          </Button>,
        ]}
        width={650}
      >
        {viewingDbCluster && (
          <div className="db-cluster-details">
            {/* Header Box */}
            <div
              style={{
                background: "#f0f7ff",
                padding: "16px",
                borderRadius: "8px",
                marginBottom: "20px",
                border: "1px solid #bae7ff",
              }}
            >
              <div
                style={{
                  color: "#8c8c8c",
                  fontSize: "12px",
                  textTransform: "uppercase",
                }}
              >
                Cluster Name
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#003a8c",
                }}
              >
                {viewingDbCluster.cluster_name}
              </div>
            </div>

            {/* Section 1: General Info */}
            <Divider orientation="left" style={{ margin: "12px 0" }}>
              General Information
            </Divider>
            <Row gutter={[24, 20]}>
              <Col span={12}>
                <div
                  style={{
                    color: "#8c8c8c",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  Status
                </div>
                <Tag
                  color={
                    ["enabled", "active"].includes(
                      String(viewingDbCluster.status || "")
                        .toLowerCase()
                        .trim(),
                    )
                      ? "success"
                      : "error"
                  }
                  style={{ borderRadius: "4px", padding: "2px 10px" }}
                >
                  {["enabled", "active"].includes(
                    String(viewingDbCluster.status || "")
                      .toLowerCase()
                      .trim(),
                  )
                    ? "Active"
                    : "Inactive"}
                </Tag>
              </Col>
              <Col span={12}>
                <div
                  style={{
                    color: "#8c8c8c",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  Cluster ID
                </div>
                <div
                  style={{
                    fontFamily: "monospace",
                    background: "#f5f5f5",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    display: "inline-block",
                  }}
                >
                  {viewingDbCluster.cluster_id}
                </div>
              </Col>
            </Row>

            {/* Section 2: Workspace Info */}
            <Divider orientation="left" style={{ margin: "24px 0 12px 0" }}>
              Workspace Details
            </Divider>
            <Row gutter={[24, 20]}>
              <Col span={12}>
                <div
                  style={{
                    color: "#8c8c8c",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  Workspace Name
                </div>
                <div style={{ fontWeight: "500" }}>
                  {viewingDbCluster.workspace_name || "N/A"}
                </div>
              </Col>
              <Col span={12}>
                <div
                  style={{
                    color: "#8c8c8c",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  Workspace ID
                </div>
                <div style={{ fontWeight: "500" }}>
                  {viewingDbCluster.workspace_id}
                </div>
              </Col>
              <Col span={12}>
                <div
                  style={{
                    color: "#8c8c8c",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  Deployment Name
                </div>
                <div style={{ fontWeight: "500" }}>
                  {viewingDbCluster.deployment_name || "N/A"}
                </div>
              </Col>
            </Row>

            {/* Section 3: Security */}
            <Divider orientation="left" style={{ margin: "24px 0 12px 0" }}>
              Security Configuration
            </Divider>
            <Row gutter={[24, 20]}>
              <Col span={24}>
                <div
                  style={{
                    color: "#8c8c8c",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  Personal Access Token (PAT)
                </div>
                <div
                  style={{
                    background: "#fffbe6",
                    border: "1px solid #ffe58f",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                  }}
                >
                  {viewingDbCluster.pat_token
                    ? `dapi${"*".repeat(16)}${viewingDbCluster.pat_token.slice(-4)}`
                    : "No token provided"}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#faad14",
                    marginTop: "4px",
                  }}
                >
                  Token is partially masked for your security.
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
      {/* Edit Cluster Metrics Modal */}
      <Modal
        title="Edit Cluster Metrics"
        open={editDbModalVisible}
        onCancel={() => setEditDbModalVisible(false)}
        onOk={() => dbEditForm.submit()}
        confirmLoading={loading}
        width={600}
        okText="Update"
      >
        <Form
          form={dbEditForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              setLoading(true);
              const accountId =
                selectedDatabricksAccount?.accountId ||
                selectedAccount?.replace("databricks_", "");
              const accountName = selectedDatabricksAccount?.accountName;
              await databricksService.toggleMetrics(
                values.workspace_id,
                values.deployment_name,
                editingDbCluster.cluster_id,
                true,
                accountId,  // Pass account_id as fallback
                accountName,  // Prefer account_name for OAuth token generation
                values.pat_token || null,  // PAT token optional - backend will use OAuth if not provided
                values.instance_profile_arn || null,  // instance_profile_arn for enabling
              );
              message.success("Cluster metrics updated successfully");
              setEditDbModalVisible(false);
              fetchDbClusters();
            } catch (error) {
              message.error("Failed to update cluster");
            } finally {
              setLoading(false);
            }
          }}
        >
          <Form.Item
            name="deployment_name"
            label="Deployment Name"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="pat_token"
            label="Token"
            rules={[{ required: true }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MetricsPage;
